import { AxiosRequestConfig } from 'axios';
import Page from 'classes/page';
import PriceCalculator from 'classes/price-calculator';
import Product from 'classes/product';
import Template from 'classes/template';
import TemplatePosition from 'classes/templateposition';
import Theme from 'classes/theme';
import User from 'classes/user';
import ButtonComponent from 'components/button';
import EventBus from 'components/event-bus';
import analytics from 'controllers/analytics';
import experiment from 'controllers/experiment';
import navigate from 'controllers/navigate';
import storage from 'controllers/storage';
import template from 'controllers/template';
import upload from 'controllers/upload';
import type {
	DialogClosePayloadVirtualOfferingSelect,
	DynamicFillBaseOptionPricing,
	EditorBookFillImagePageOrientation,
	EditorBookFillMargins,
	EditorBookFillPage,
	EditorBookFillPages,
	GridPhotoData,
	ProductStateAddPageOptions,
	TemplatePhotoPosition,
	TemplateTextPosition,
} from 'interfaces/app';
import type * as DB from 'interfaces/database';
import type * as PI from 'interfaces/project';
import moment from 'moment-timezone';
import deleteProduct from 'mutations/product/delete';
import checkConnection from 'services/check-connection';
import * as DialogService from 'services/dialog';
import {
	ERRORS_LOAD_FONT,
	ERRORS_OFFLINE,
} from 'settings/errors';
import {
	OfferingConversions,
	OfferingGroups,
	OfferingLaunchers,
} from 'settings/offerings';
import {
	AppDataModule,
	AppStateModule,
	CartItemsModule,
	ConfigModule,
	FontModule,
	PhotosModule,
	ProductsModule,
	ProductStateModule,
	ThemeDataModule,
	ThemeStateModule,
	UIModule,
	UploadModule,
	UserModule,
} from 'store';
import { dialogSlot } from 'store/ui';
import { projectTools } from 'tools';
import getRandomString from 'tools/get-random-string';
import _ from 'underscore';
import {
	fontUtils,
	textUtils,
} from 'utils';
import CloneView from 'views/clone';
import EditorBookFillView from 'views/editor-book-fill';
import LayoutPickerView from 'views/layout-picker';
import ProductAttributesView from 'views/product-attributes';
import SavingProjectView from 'views/saving-project';
import SmartEnhancementView from 'views/smart-enhancement';
import UploadErrorsView from 'views/upload-errors';
import VirtualOfferingSelectView from 'views/virtual-offering-select';

moment.tz.setDefault('Europe/Amsterdam');

export default class ProductState {
	private static savePromise: Promise<void> | null = null;

	public static autoFill(
		pageCount?: number,
		options?: {
			pushHistory?: boolean;
		},
	): Promise<void> {
		// Temporarily disable auto saving
		AppStateModule.disableAutoSave();

		// fill template with photos not yet included
		return template
			.autoFill(
				pageCount,
				options,
			)
			.catch((e) => {
				if (typeof window.glBugsnagClient !== 'undefined') {
					const err = new Error(`error in auto filling: ${e}`);
					window.glBugsnagClient.notify(
						err,
						(event) => { event.severity = 'error'; },
					);
				}
			})
			.finally(() => {
				// Enable auto saving again
				AppStateModule.enableAutoSave();

				// Save result
				ProductState.save();

				// Move forward in process
				navigate.goForward('fill');
			});
	}

	static projectContentInsights = () => {
		const groupId = ProductStateModule.getProduct?.group;

		if (!groupId) {
			throw new Error('Missing group id of project');
		}

		const isBook = OfferingGroups(
			groupId,
			['BookTypes'],
		);

		// Get all editable pages in the project
		const editablePages = ProductStateModule.getEditablePages;

		// Apply filter for books to only include inner pages
		const filteredEditablePages = isBook
			? _.filter(
				editablePages,
				(pageModel) => ProductStateModule.getPageIndex(pageModel) > 1,
			)
			: editablePages;

		const emptyPages = _.filter(
			filteredEditablePages,
			(pageModel) => {
				const pageObjects = ProductStateModule.getPageObjects(pageModel);
				return pageObjects.length == 0;
			},
		);

		return {
			totalEditablePages: filteredEditablePages.length,
			totalEmptyPages: emptyPages.length,
		};
	};

	static create(
		data: Partial<DB.ProductModel>,
		projectSettings?: Partial<PI.ProductSettings>,
		customAttributes?: PI.ProductDataModel['customAttributes'],
	): Promise<DB.ProductModel> {
		let affiliateid = 0;

		if (window.affiliateID >= 0) {
			affiliateid = window.affiliateID;
		} else if (UserModule.affiliateid) {
			({ affiliateid } = UserModule);
		}

		data = _.extend(
			{
				core: 2,
				userid: UserModule.id,
				read_token: getRandomString(20),
				affiliateid,
			},
			data,
		);

		if (typeof data.layoutid === 'undefined' && data.themeid) {
			const themeModel = ThemeDataModule.getTheme(data.themeid);

			if (themeModel && themeModel.layoutid) {
				data.layoutid = themeModel.layoutid;
			}
		}

		const searchProps: Partial<DB.OfferingModel> = {
			groupid: data.group,
			typeid: data.typeid,
		};

		if (data.variantid) {
			searchProps.variantid = data.variantid;
		}

		const offeringModel = AppDataModule.findOfferingWhere(searchProps);

		if (!offeringModel) {
			return Promise.reject(new Error('Could not find offering model'));
		}
		if (!offeringModel.instock) {
			return Promise.reject(new Error(window.App.router.$t('offeringNotInStock')));
		}

		// Reset the productstate data and cleanup listeners
		return ProductState
			.reset()
			.then(() => ProductsModule.createModel({
				data,
			}))
			.then((productModel) => {
				analytics.trackCreateProject(productModel);

				return ProductState.select(productModel.id);
			})
			.then((productModel) => {
				ProductStateModule.resetHistory();

				if (projectSettings) {
					ProductStateModule.changeProductSettings(
						projectSettings,
					);
				}

				if (customAttributes) {
					ProductStateModule.setCustomAttributes(
						customAttributes,
					);
				}

				return productModel;
			});
	}

	static askEnhancementConfirmation(): Promise<void> {
		/*
		 * If the offering supports neither enhancement and upscaling,
		 * there's no need to ask the user for this setting
		 */
		const offeringModel = ProductStateModule.getOffering;

		if (
			offeringModel?.type === 'photo'
			&& (
				offeringModel.applyEnhancement
				|| offeringModel.applyUpscaling
			)
		) {
			return new Promise((resolve) => {
				const settings = ProductStateModule.getProductSettings;
				let smartEnhancement = true;

				if (typeof settings.applyEnhancement !== 'undefined') {
					smartEnhancement = settings.applyEnhancement;
				}

				UIModule.openDialog({
					header: dialogSlot({
						props: {
							class: 'md:text-(length:--font-size-l)',
						},
						slot: window.App.router.$t('views.smartEnhancement.title'),
					}),
					body: dialogSlot({
						tag: SmartEnhancementView,
						props: {
							class: 'min-w-full',
							showSwitchBox: true,
							value: smartEnhancement,
							'onUpdate:value': (value) => {
								smartEnhancement = value;
							},
						},
					}),
					footer: [
						dialogSlot({
							tag: ButtonComponent,
							slot: window.App.router.$t('views.smartEnhancement.saveButton'),
							props: {
								class: 'grow',
								height: 'calc(var(--spacing) * 12.5)',
								onClick: () => {
									const enhancedData: Partial<PI.ProductSettings> = {
										applyEnhancement: smartEnhancement,
									};
									ProductStateModule.changeProductSettings(enhancedData);

									analytics.trackEvent(
										'Save enhancement setting',
										{
											enabled: smartEnhancement,
										},
									);
									UIModule.closeDialog();
									resolve();
								},
							},
						}),
					],
					props: {
						hasCloseButton: false,
						class: 'md:w-115',
					},
				});
			});
		}

		return Promise.resolve();
	}

	static autoConvertProjectOffering(
		productid: number,
	): Promise<void> {
		// This feature is currently only available using an experiment flag
		if (!experiment.isFeatureEnabled('flag_offering_conversion')) {
			return Promise.resolve();
		}

		// Make sure the currently opened project is the same as the one we are trying to convert
		const productModel = ProductStateModule.getProduct;

		if (productModel?.id != productid) {
			return Promise.reject(
				new Error('ProjectId did not match with current active project'),
			);
		}

		// We need the offering model to know from which offering we are converting
		const offeringModel = ProductStateModule.getOffering;

		if (!offeringModel) {
			return Promise.reject(
				new Error('Could not find offering model'),
			);
		}

		// Check the offering mapping
		const newOfferingId = OfferingConversions(
			offeringModel.id,
		);

		// If no mapping is found, we can exit without the need to convert
		if (!newOfferingId) {
			return Promise.resolve();
		}

		// Show dialog to user to notify about the conversion we will perform
		return new Promise((resolve, reject) => {
			const closeAlert = DialogService.openAlertDialog({
				header: {
					title: window.App.router.$t('dialogs.autoConvertProjectOffering.title'),
				},
				body: {
					content: window.App.router.$t('dialogs.autoConvertProjectOffering.message'),
				},
				footer: {
					buttons: [
						{
							id: 'accept',
							text: window.App.router.$t('dialogs.autoConvertProjectOffering.buttons.accept'),
							click: () => {
								// Close the alert dialog
								closeAlert();

								// Change the offering to the mapped one
								if (newOfferingId) {
									ProductState.changeOffering(
										newOfferingId,
									).then(
										resolve,
										reject,
									);
								} else {
									reject(new Error('Could not find new offering id'));
								}
							},
						},
					],
				},
			});
		});
	}

	static async setup(
		productid: number,
		options?: {
			ajaxOptions?: AxiosRequestConfig;
			customAttributes?: PI.ProductDataModel['customAttributes'];
			extendProjectLifetime?: boolean;
		},
	): Promise<void> {
		if (ProductsModule.isCollectionBusy) {
			try {
				await ProductsModule.waitForCollectionIdle();
			} catch {
				/**
				 * Continue with the existing setup guard and report the final state there.
				 */
			}
		}

		// check if product model is already loaded
		if (ProductStateModule.productId == productid) {
			if (ProductStateModule.getProduct) {
				/**
				 * Productstate already setup for this product, continue
				 */
				return Promise.resolve();
			}

			const error = new Error('Product model missing during productstate setup');
			window.glBugsnagClient?.notify(
				error,
				(event) => {
					event.severity = 'error';
					event.addMetadata(
						'productstate',
						{
							productId: productid,
							currentProductId: ProductStateModule.productId,
							productCollectionSize: ProductsModule.models.length,
							productCollectionIds: ProductsModule.models
								.slice(
									0,
									10,
								)
								.map((model) => model.id),
							collectionFetchesInFlight: ProductsModule.collectionFetchesInFlight,
							collectionResetsInFlight: ProductsModule.collectionResetsInFlight,
							pageListLength: ProductStateModule.pageList.length,
							pageModelCount: Object.keys(ProductStateModule.pages).length,
							objectModelCount: Object.keys(ProductStateModule.objects).length,
							photoListLength: ProductStateModule.photoList.length,
							saved: ProductStateModule.getSaved,
							sync: AppStateModule.sync,
							url: window.location.href,
						},
					);
				},
			);

			return Promise.reject(
				error,
			);
		}

		if (!AppStateModule.online) {
			return Promise.reject(
				new Error(ERRORS_OFFLINE),
			);
		}

		// Online and saved product: get data from server
		const closeLoader = DialogService.openLoaderDialog();

		// Reset product state data
		return ProductState
			.reset()
			// Clear local user data to avoid memory overload
			.then(User.clearMemory)
			.then(() => (
				ProductStateModule.fetch({
					productId: productid,
					requestOptions: (
						options?.ajaxOptions
						|| undefined
					),
					parse: true,
				})
			))
			.then(() => ProductState.autoConvertProjectOffering(productid))
			.then(() => {
				if (options?.customAttributes) {
					ProductStateModule.mergeCustomAttributes(options.customAttributes);
				} else if (options?.extendProjectLifetime) {
					/*
					 * Force save the snapshot with an updated 'lastupdate' property value
					 * This will extend the lifetime of the project before automatically
					 * cleaning the data from the storage
					 */
					return ProductStateModule.save();
				}

				return undefined;
			})
			.then(closeLoader)
			.catch((err) => {
				closeLoader();

				if (err.message == ERRORS_LOAD_FONT) {
					return new Promise((resolve, reject) => {
						const closeError = DialogService.openErrorDialog({
							body: {
								content: window.App.router.$t('dialogTextLoadError'),
							},
							footer: {
								buttons: [
									{
										id: 'accept',
										text: window.App.router.$t('dialogButtonOk'),
										click: () => {
											ProductState
												.setup(
													productid,
													options,
												).then(
													resolve,
													reject,
												);
											closeError();
										},
									},
								],
							},
						});
					});
				}

				throw err;
			});
	}

	static async select(productid: number): Promise<DB.ProductModel> {
		if (ProductStateModule.productId != productid) {
			// We are switching products, execute reset for memory management
			return ProductState
				.reset()
				.then(() => (
					ProductStateModule.fetch({
						productId: productid,
						parse: true,
					})
				))
				.then(() => ProductState.select(productid))
				.catch((err) => {
					if (err.message === ERRORS_LOAD_FONT) {
						return new Promise((resolve, reject) => {
							const closeError = DialogService.openErrorDialog({
								body: {
									content: window.App.router.$t('dialogTextLoadError'),
								},
								footer: {
									buttons: [
										{
											id: 'accept',
											text: window.App.router.$t('dialogButtonOk'),
											click: () => {
												ProductState
													.select(productid)
													.then(
														resolve,
														reject,
													);
												closeError();
											},
										},
									],
								},
							});
						});
					}

					return Promise.reject(err);
				});
		}

		const productModel = ProductStateModule.getProduct;
		const offeringModel = ProductStateModule.getOffering;

		if (!productModel) {
			return Promise.reject(
				new Error('Could not find product model'),
			);
		}
		if (window.glPlatform == 'server') {
			// On the server platform there is no need to setup the theme
			return Promise.resolve(productModel);
		}

		if (!offeringModel) {
			return Promise.reject(
				new Error('Could not find offering model'),
			);
		}

		// Set showing of bleed margin to correct setting
		if (offeringModel && offeringModel.showbleed > 1) {
			AppStateModule.enableBleed();
		} else {
			AppStateModule.hideBleed();
		}

		// If themestate is undefined, fetch theme data from server
		return Theme
			.setup(productModel.themeid)
			.catch((err) => {
				/*
				 * Theme fetch failed
				 * Check if we are still online, or that offline status has been detected during the fetch
				 */
				if (!AppStateModule.online) {
					// No longer online, so reject the fetch
					return Promise.reject(
						new Error(ERRORS_OFFLINE),
					);
				}

				if (typeof window.glBugsnagClient !== 'undefined') {
					window.glBugsnagClient.notify(
						new Error(`Could not fetch theme data for theme ${productModel.themeid}`),
						(event) => { event.severity = 'error'; },
					);
				}

				if (err.response?.status === 410) {
					// Theme no longer exists, try to find an automatic replacement
					if (offeringModel?.themeid) {
						return ProductState.changeTheme(
							offeringModel.themeid,
							false,
							true,
						);
					}

					const themeModels = ThemeDataModule.getThemesByOfferingId(
						offeringModel.id,
					);

					if (themeModels.length) {
						const themeModel = themeModels[0];
						return ProductState.changeTheme(
							themeModel.id,
							false,
							true,
						);
					}
				}

				/*
				 * Still online, so there must be something wrong with the data fetch
				 * Offer error dialog to let user try again
				 */
				return new Promise((resolve, reject) => {
					let dialogErrorText = window.App.router.$t('dialogTextError');

					if (err.message) {
						dialogErrorText += `\n\nError: ${err.message}`;
					}

					const closeError = DialogService.openErrorDialog({
						body: {
							content: dialogErrorText,
						},
						footer: {
							buttons: [
								{
									id: 'abort',
									text: window.App.router.$t('dialogButtonErrorAbort'),
									click: () => {
										closeError();
										reject(err);
									},
								},
								{
									id: 'accept',
									text: window.App.router.$t('dialogButtonErrorRetry'),
									click: () => {
										closeError();

										ProductState
											.select(productid)
											.then(
												resolve,
												reject,
											);
									},
								},
							],
						},
					});
				});
			})
			.then(() => {
				/**
				 * Theme-level backend-only fixed pages already count towards the setup-page total,
				 * so use the same effective page count as the rest of the min/max validation flow.
				 */
				const extraPageQuantity = offeringModel.setuppages - ProductStateModule.getPageCountForLimitChecks;

				if (extraPageQuantity <= 0) {
					try {
						// Automatically update project content from its offering options
						this.updateContentFromOfferingOptions();
					} catch {
						// Do nothing
					}

					return productModel;
				}

				const promiseAll: Promise<PI.PageModel>[] = [];

				for (let y = 0; y < extraPageQuantity; y += 1) {
					promiseAll.push(
						ProductState.addPage({
							autoCompensate: false,
						}),
					);
				}

				return Promise
					.all(promiseAll)
					.then(() => (
						ProductState.select(productid)
					))
					.catch((err) => (
						ProductState
							.reset()
							.then(() => (
								Promise.reject(err)
							))
					));
			})
			.then(async () => {
				await projectTools.checkUpdateSpine(offeringModel);

				return productModel;
			});
	}

	static reset(): Promise<void> {
		// Remove upload items from queues in upload controller
		upload.reset();

		// Reset store
		return ProductStateModule.reset();
	}

	public static scalePage(
		pageModel: PI.PageModel,
		target: {
			offeringId?: number,
			width: number,
			height: number,
			bleedMargin: number,
			configDpi: number,
			minimumDpi: number,
			qualityDpi: number,
			minFontSize: number,
		},
	) {
		const sourceWidth = pageModel.width;
		const sourceHeight = pageModel.height;
		const sourceOffset = pageModel.offset;
		const targetOffset = target.bleedMargin;
		const scaleFactorWidth = (target.width + 2 * targetOffset) / (sourceWidth + 2 * sourceOffset);
		const scaleFactorHeight = (target.height + 2 * targetOffset) / (sourceHeight + 2 * sourceOffset);
		const scaleHorizontalCoordinate = projectTools.createScaleHorizontalCoordinateFn(
			sourceWidth,
			sourceOffset,
			target,
			targetOffset,
		);
		const scaleVerticalCoordinate = projectTools.createScaleVerticalCoordinateFn(
			sourceHeight,
			sourceOffset,
			target,
			targetOffset,
		);

		const coordinateSnapThreshold = 2;
		const pageProps: OptionalExceptFor<PI.PageModel, 'id'> = {
			id: pageModel.id,
			width: target.width,
			height: target.height,
			offset: target.bleedMargin,
		};

		if (target.offeringId) {
			pageProps.offeringId = target.offeringId;
		}

		ProductStateModule.changePage(pageProps);

		ProductStateModule
			.getPageObjects(pageModel)
			.forEach((objectModel) => {
				const scaledLeft = scaleHorizontalCoordinate(objectModel.x_axis);
				const scaledRight = scaleHorizontalCoordinate(
					objectModel.x_axis + objectModel.width,
				);
				const scaledTop = scaleVerticalCoordinate(objectModel.y_axis);
				const scaledBottom = scaleVerticalCoordinate(
					objectModel.y_axis + objectModel.height,
				);
				const props: OptionalExceptFor<PI.PageObjectModel, 'id' | 'x_axis' | 'y_axis' | 'width' | 'height'> = {
					id: objectModel.id,
					x_axis: scaledLeft,
					y_axis: scaledTop,
					width: scaledRight - scaledLeft,
					height: scaledBottom - scaledTop,
				};

				/**
				 * Auto compensate for small rounding differences
				 *
				 * Preserve exact alignment to every meaningful boundary. We need to keep objects
				 * flush with:
				 * - `-targetOffset`: start of the leading bleed margin.
				 * - `0`: trim origin, i.e. the first printable point.
				 * Without re-snapping to those two anchors, a full-bleed object could end up at
				 * e.g. `-29.8` or `-0.8` even though it visually belongs on the boundary.
				 */
				const horizontalSnapTargets = [
					0,
					-targetOffset,
				];
				/**
				 * Same set of anchors for the vertical axis (top bleed and trim origin) to keep
				 * scaled coordinates consistent across both dimensions.
				 */
				const verticalSnapTargets = [
					0,
					-targetOffset,
				];

				if (typeof props.x_axis === 'number') {
					const horizontalMatch = horizontalSnapTargets.find((boundary) => (
						Math.abs(props.x_axis - boundary) <= coordinateSnapThreshold
					));

					if (typeof horizontalMatch === 'number') {
						props.x_axis = horizontalMatch;
					}
				}

				if (typeof props.y_axis === 'number') {
					const verticalMatch = verticalSnapTargets.find((boundary) => (
						Math.abs(props.y_axis - boundary) <= coordinateSnapThreshold
					));

					if (typeof verticalMatch === 'number') {
						props.y_axis = verticalMatch;
					}
				}
				/**
				 * Width/height represent the delta between two coordinates that each snap to
				 * bleed/trim anchors. Re-snap the dimensions themselves so a full-bleed object
				 * ends up at the canonical sizes (trim-to-trim, trim-to-bleed, bleed-to-bleed).
				 */
				if (typeof props.width === 'number') {
					const widthTargets = [
						target.width,
						target.width + targetOffset,
						target.width + 2 * targetOffset,
					];
					const widthMatch = widthTargets.find((size) => (
						Math.abs(props.width - size) <= coordinateSnapThreshold
					));

					if (typeof widthMatch === 'number') {
						props.width = widthMatch;
					}
				}

				if (typeof props.height === 'number') {
					const heightTargets = [
						target.height,
						target.height + targetOffset,
						target.height + 2 * targetOffset,
					];
					const heightMatch = heightTargets.find((size) => (
						Math.abs(props.height - size) <= coordinateSnapThreshold
					));

					if (typeof heightMatch === 'number') {
						props.height = heightMatch;
					}
				}

				if (objectModel.borderwidth) {
					props.borderwidth = objectModel.borderwidth * Math.min(
						scaleFactorWidth,
						scaleFactorHeight,
					);
				}

				if (
					objectModel.type === 'photo'
					&& objectModel.photoid
				) {
					const photoModel = PhotosModule.getById(
						objectModel.photoid,
					);

					if (!photoModel) {
						return;
					}

					const facebox = (
						(
							typeof photoModel.fcx === 'number'
							&& typeof photoModel.fcy === 'number'
							&& typeof photoModel.fcw === 'number'
							&& typeof photoModel.fch === 'number'
						)
							? {
								x: photoModel.fcx,
								y: photoModel.fcy,
								width: photoModel.fcw,
								height: photoModel.fch,
							}
							: undefined
					);
					const calc = Template.fitPhotoInRectangle(
						{
							x: props.x_axis,
							y: props.y_axis,
							width: props.width,
							height: props.height,
							angle: objectModel.rotate,
							borderwidth: objectModel.borderwidth,
							autoRotate: false,
						},
						{
							id: photoModel.id,
							width: photoModel.full_width,
							height: photoModel.full_height,
							facebox,
						},
						undefined,
						{
							fit: photoModel.type === 'logo',
							resizing: {
								maxScale: target.configDpi / target.minimumDpi,
								recommendedMaxScale: target.configDpi / target.qualityDpi,
							},
						},
					);

					props.x_axis = calc.x;
					props.y_axis = calc.y;
					props.width = calc.width;
					props.height = calc.height;
					props.cropx = calc.cropX;
					props.cropy = calc.cropY;
					props.cropwidth = calc.cropWidth;
					props.cropheight = calc.cropHeight;
				}

				if (
					objectModel.type == 'text'
					&& (
						objectModel.pointsize
						|| objectModel.fontsize
					)
				) {
					if (!objectModel.fontface) {
						throw new Error('Missing required fontface');
					}

					const temporaryNewPageObject: PI.PageObjectModel = {
						...objectModel,
						...props,
					};
					const fitResult = textUtils.fit({
						fontFamily: fontUtils.getFontFamily({
							family: temporaryNewPageObject.fontface!,
							bold: !!temporaryNewPageObject.fontbold,
							italic: !!temporaryNewPageObject.fontitalic,
						}),
						fontModel: FontModule.getById(temporaryNewPageObject.fontface!),
						fontSize: (
							temporaryNewPageObject.fontsize
							?? fontUtils.pointToPixel(temporaryNewPageObject.pointsize!)
						),
						isBold: !!temporaryNewPageObject.fontbold,
						maxWidth: temporaryNewPageObject.width,
						text: temporaryNewPageObject.text || '',
						kind: 'font',
						boxHeight: temporaryNewPageObject.height,
						boxWidth: temporaryNewPageObject.width,
						minFontSize: (
							target.minFontSize
							?? fontUtils.MIN_FONTSIZE
						),
						maxFontSize: pageModel.height,
					});

					/**
					 * Only adjust height of text object when it is transformable
					 */
					if (objectModel.transformable) {
						props.height = Math.max(
							props.height,
							fitResult.height,
						);
					}

					props.fontsize = fitResult.usedFontSize;
				}

				ProductStateModule.changePageObject(props);
			});
	}

	public static scaleSpine(
		spineModel: PI.PageModel,
		target: {
			height: number,
			bleedMargin: number,
			configDpi: number,
			minimumDpi: number,
			qualityDpi: number,
			minFontSize: number,
		},
	) {
		const sourceHeight = spineModel.height;
		const sourceOffset = spineModel.offset;
		const targetOffset = target.bleedMargin;
		const scaleVerticalCoordinate = projectTools.createScaleVerticalCoordinateFn(
			sourceHeight,
			sourceOffset,
			target,
			targetOffset,
		);

		const coordinateSnapThreshold = 2;
		const spineProps: OptionalExceptFor<PI.PageModel, 'id'> = {
			id: spineModel.id,
			height: target.height,
			offset: target.bleedMargin,
		};

		ProductStateModule.changePage(spineProps);

		ProductStateModule
			.getPageObjects(spineModel)
			.forEach((objectModel) => {
				const scaledTop = scaleVerticalCoordinate(objectModel.y_axis);
				const scaledBottom = scaleVerticalCoordinate(
					objectModel.y_axis + objectModel.height,
				);
				const props: OptionalExceptFor<PI.PageObjectModel, 'id' | 'y_axis' | 'height'> = {
					id: objectModel.id,
					y_axis: scaledTop,
					height: scaledBottom - scaledTop,
				};

				/**
				 * Auto compensate for small rounding differences
				 *
				 * Preserve exact alignment to every meaningful boundary. We need to keep objects
				 * flush with:
				 * - `-targetOffset`: start of the leading bleed margin.
				 * - `0`: trim origin, i.e. the first printable point.
				 * Without re-snapping to those two anchors, a full-bleed object could end up at
				 * e.g. `-29.8` or `-0.8` even though it visually belongs on the boundary.
				 */
				const verticalSnapTargets = [
					0,
					-targetOffset,
				];

				if (typeof props.y_axis === 'number') {
					const verticalMatch = verticalSnapTargets.find((boundary) => (
						Math.abs(props.y_axis - boundary) <= coordinateSnapThreshold
					));

					if (typeof verticalMatch === 'number') {
						props.y_axis = verticalMatch;
					}
				}

				/**
				 * Height represent the delta between two coordinates that each snap to
				 * bleed/trim anchors. Re-snap the dimensions themselves so a full-bleed object
				 * ends up at the canonical sizes (trim-to-trim, trim-to-bleed, bleed-to-bleed).
				 */
				if (typeof props.height === 'number') {
					const heightTargets = [
						target.height,
						target.height + targetOffset,
						target.height + 2 * targetOffset,
					];
					const heightMatch = heightTargets.find((size) => (
						Math.abs(props.height - size) <= coordinateSnapThreshold
					));

					if (typeof heightMatch === 'number') {
						props.height = heightMatch;
					}
				}

				if (
					objectModel.type == 'text'
					&& (
						objectModel.pointsize
						|| objectModel.fontsize
					)
				) {
					if (!objectModel.fontface) {
						throw new Error('Missing required fontface');
					}

					const temporaryNewPageObject: PI.PageObjectModel = {
						...objectModel,
						...props,
					};
					const fitResult = textUtils.fit({
						fontFamily: fontUtils.getFontFamily({
							family: temporaryNewPageObject.fontface!,
							bold: !!temporaryNewPageObject.fontbold,
							italic: !!temporaryNewPageObject.fontitalic,
						}),
						fontModel: FontModule.getById(temporaryNewPageObject.fontface!),
						fontSize: (
							temporaryNewPageObject.fontsize
							?? fontUtils.pointToPixel(temporaryNewPageObject.pointsize!)
						),
						isBold: !!temporaryNewPageObject.fontbold,
						maxWidth: temporaryNewPageObject.width,
						text: temporaryNewPageObject.text || '',
						kind: 'font',
						boxHeight: temporaryNewPageObject.height,
						boxWidth: temporaryNewPageObject.width,
						minFontSize: (
							target.minFontSize
							?? fontUtils.MIN_FONTSIZE
						),
						maxFontSize: spineModel.height,
					});

					/**
					 * Only adjust height of text object when it is transformable
					 */
					if (objectModel.transformable) {
						props.height = Math.max(
							props.height,
							fitResult.height,
						);
					}

					props.fontsize = fitResult.usedFontSize;
				}

				ProductStateModule.changePageObject(props);
			});
	}

	/**
	 * Automatically makes updates to the content of the project based on the offering options
	 * Currently only in use for selecting a cover color, but can be extended to other offering options
	 *
	 * @returns void
	 */
	static updateContentFromOfferingOptions(): void {
		/*
		 * This is currently not in use, but this function could be plugged in
		 * for special type of offerings in the future
		 */
	}

	/**
	 * Change the offering and corresponding theme of the current project
	 * @param offeringid New offering id to change to
	 * @param options Options for changing the offering
	 * @param options.pushHistory If `true`, a history state will be pushed after the offering change. Default is `true`.
	 * @returns Promise that resolves when the offering change is completed
	 */
	public static async changeOffering(
		offeringid: number,
		options?: {
			pushHistory?: boolean;
		},
	): Promise<void> {
		const { productId } = ProductStateModule;

		if (!productId) {
			throw new Error('Missing required productId');
		}

		const currentOfferingModel = ProductStateModule.getOffering;

		if (!currentOfferingModel) {
			throw new Error('Missing required current offering model');
		}

		const newOfferingModel = AppDataModule.getOffering(offeringid);

		if (!newOfferingModel) {
			throw new Error('Missing required new offering model');
		}

		// Set flag to avoid auto saving data while we are changing it, so there's not intermediate result saved to the server
		AppStateModule.disableAutoSave();

		try {
			// Change the offering properties of the product model in the database
			await ProductsModule.putModel({
				id: productId,
				data: {
					group: newOfferingModel.groupid,
					typeid: newOfferingModel.typeid,
					variantid: newOfferingModel.variantid,
				},
			});

			// Reset the cached mask image so it will be reloaded after the offering change
			ProductStateModule.resetMaskImage();
			AppStateModule.setMask(null);

			// Reset cached images of the page objects so they will be reloaded
			if (currentOfferingModel.previewColorSpectrum !== newOfferingModel.previewColorSpectrum) {
				const objectModels = ProductStateModule.getObjects;

				for (const objectModel of objectModels) {
					if (objectModel._image) {
						await ProductStateModule.changePageObject({
							id: objectModel.id,
							_resetImage: true,
						});
					}
				}
			}

			// Fetch the theme data from the server
			await Theme.fetchData({
				showLoader: false,
				flexgroupid: (
					newOfferingModel.flexgroupid
					?? undefined
				),
				groupid: (
					newOfferingModel.groupid
					?? undefined
				),
				typeid: (
					newOfferingModel.typeid
					?? undefined
				),
			});

			let themeModel: DB.ThemeModel | undefined;

			if (newOfferingModel.themeid) {
				// The offering has a default theme defined, so we will use that theme
				themeModel = ThemeDataModule.getTheme(newOfferingModel.themeid);
			}

			if (!themeModel) {
				// There is no default theme defined, so we will just use the first one available
				const themeModels = ThemeDataModule.getThemesByOfferingId(offeringid);

				if (themeModels.length) {
					[themeModel] = themeModels;
				}
			}

			if (!themeModel) {
				// There are no theme models available for this offering
				throw new Error('Missing required theme model');
			}

			const isBasicProduct = OfferingGroups(
				newOfferingModel.groupid,
				['BasicProducts'],
			);

			await this.changeTheme(
				themeModel.id,
				false,
				!isBasicProduct,
				options?.pushHistory,
			);

			if (ProductStateModule.getPageCountForLimitChecks > newOfferingModel.maxpages) {
				/**
				 * Re-evaluate max-page trimming after the theme switch so backend-only fixed pages
				 * are counted from the target theme and not from the previous theme.
				 */
				const toRemoveQuantity = ProductStateModule.getPageCountForLimitChecks - newOfferingModel.maxpages;
				for (let y = 0; y < toRemoveQuantity; y += 1) {
					await ProductStateModule.removePage({
						pageId: ProductStateModule.pageList[ProductStateModule.pageList.length - 1],
						keepPhotos: true,
					});
				}
			}

			for (const pageModel of ProductStateModule.getPages) {
				const themePage = ThemeStateModule.getPageStateData(
					ProductStateModule.getPageIndex(pageModel),
					0,
					{},
				);

				this.scalePage(
					pageModel,
					{
						offeringId: (ProductStateModule.getOffering?.virtual ? newOfferingModel.id : undefined),
						width: (themePage.pageModel.width ?? newOfferingModel.width),
						height: (themePage.pageModel.height ?? newOfferingModel.height),
						bleedMargin: (themePage.pageModel.offset ?? newOfferingModel.bleedmargin),
						configDpi: newOfferingModel.configdpi,
						minimumDpi: newOfferingModel.minimumdpi,
						qualityDpi: newOfferingModel.qualitydpi,
						minFontSize: fontUtils.pointToPixel(newOfferingModel.minfontsize),
					},
				);
			}

			if (ProductStateModule.getSpine) {
				const themePage = ThemeStateModule.getPageStateData(
					0,
					0,
					{},
				);
				this.scaleSpine(
					ProductStateModule.getSpine,
					{
						height: (themePage.pageModel.height ?? newOfferingModel.height),
						bleedMargin: (themePage.pageModel.offset ?? newOfferingModel.bleedmargin),
						configDpi: newOfferingModel.configdpi,
						minimumDpi: newOfferingModel.minimumdpi,
						qualityDpi: newOfferingModel.qualitydpi,
						minFontSize: fontUtils.pointToPixel(newOfferingModel.minfontsize),
					},
				);
			}

			if (ProductStateModule.getPageCountForLimitChecks < newOfferingModel.minpages) {
				// We need to add one or more pages to the project, as the new offering requires more pages
				const toAddQuantity = newOfferingModel.minpages - ProductStateModule.getPageCountForLimitChecks;

				for (let y = 0; y < toAddQuantity; y += 1) {
					await ProductState.addPage({
						autoCompensate: false,
					});
				}
			}

			await projectTools.checkUpdateSpine(newOfferingModel);

			try {
				// Automatically update project content from its offering options
				this.updateContentFromOfferingOptions();
			} catch {
				// Do nothing
			}

			/*
			 * If there is an item in the cart for this project and the old offering id,
			 * we update this cart item with the new offering id
			 */
			const cartItem = CartItemsModule.findWhere({
				productid: productId,
				offeringid: currentOfferingModel.id,
			});

			if (cartItem) {
				await CartItemsModule.putModel({
					id: cartItem.id,
					data: {
						offeringid: newOfferingModel.id,
						groupid: newOfferingModel.groupid,
						typeid: newOfferingModel.typeid,
						variantid: newOfferingModel.variantid,
					},
				});
			}
		} catch (error) {
			// Re-enable auto saving
			AppStateModule.enableAutoSave();

			throw error;
		}
	}

	static changeOfferingVariant(
		variantid: number,
	): Promise<void> {
		const { productId } = ProductStateModule;

		if (!productId) {
			return Promise.reject(
				new Error('Missing required productId'),
			);
		}

		const currentOfferingModel = ProductStateModule.getOffering;

		if (!currentOfferingModel) {
			return Promise.reject(
				new Error('Missing required current offering model'),
			);
		}

		return ProductsModule.putModel({
			id: productId,
			data: {
				variantid,
			},
		}).then(() => {
			ProductStateModule.resetMaskImage();
			AppStateModule.setMask(null);

			ProductStateModule.flagChange();
		}).then(() => {
			const newOfferingModel = ProductStateModule.getOffering;

			if (!newOfferingModel) {
				return Promise.reject(
					new Error('Missing required new offering model'),
				);
			}

			try {
				// Automatically update project content from its offering options
				this.updateContentFromOfferingOptions();
			} catch {
				// Do nothing
			}

			/*
			 * If there is an item in the cart for this project and the old offering id,
			 * we update this cart item with the new offering id
			 */
			const cartItem = CartItemsModule.findWhere({
				productid: productId,
				offeringid: currentOfferingModel.id,
			});

			if (cartItem) {
				return CartItemsModule.putModel({
					id: cartItem.id,
					data: {
						offeringid: newOfferingModel.id,
						variantid: newOfferingModel.variantid,
					},
				}).then(() => undefined);
			}

			return undefined;
		});
	}

	static changePagesOfferingTypeId(
		typeId: DB.OfferingModel['typeid'] | 'bestFit',
	) {
		ProductStateModule.getPages.forEach((pageModel) => {
			this.changePageOfferingTypeId(
				pageModel,
				typeId,
			);
		});

		ProductStateModule.pushHistory();
	}

	public static changePageOfferingTypeId(
		pageModel: PI.PageModel,
		typeId: DB.OfferingModel['typeid'] | 'bestFit',
	) {
		const mainOfferingModel = ProductStateModule.getOffering;

		if (!mainOfferingModel) {
			throw new Error('Could not find required offering model');
		}

		const currentOfferingModel = (
			pageModel.offeringId
				? AppDataModule.getOffering(pageModel.offeringId)
				: undefined
		);

		let newOfferingModel: DB.OfferingModel | undefined;

		if (typeId === 'bestFit') {
			const pagePhotoModels = ProductStateModule.getPagePhotos(pageModel);

			if (pagePhotoModels.length) {
				newOfferingModel = ProductState.findBestVirtualOfferingChild(
					pagePhotoModels[0].full_width,
					pagePhotoModels[0].full_height,
					currentOfferingModel?.variantid,
				);
			}
		} else {
			newOfferingModel = AppDataModule.findOfferingWhere({
				groupid: mainOfferingModel.groupid,
				typeid: typeId,
				variantid: currentOfferingModel?.variantid || 1,
			});
		}

		if (newOfferingModel) {
			this.changePageOfferingId(
				pageModel,
				newOfferingModel.id,
				true,
			);
		}
	}

	public static changePagesOfferingId(
		offeringId: DB.OfferingModel['id'],
	) {
		ProductStateModule.getPages.forEach((pageModel) => {
			this.changePageOfferingId(
				pageModel,
				offeringId,
				true,
			);
		});

		ProductStateModule.pushHistory();
	}

	public static changePageOfferingId(
		pageModel: PI.PageModel,
		offeringId: DB.OfferingModel['id'],
		skipHistory?: boolean,
	) {
		if (pageModel.offeringId !== offeringId) {
			const selectedOfferingModel = AppDataModule.getOffering(
				offeringId,
			);

			if (!selectedOfferingModel) {
				throw new Error('Could not find selected offering model');
			}
			if (!pageModel.offeringId) {
				throw new Error('No current offeringId set to page model');
			}

			const currentOfferingModel = AppDataModule.getOffering(
				pageModel.offeringId,
			);

			if (!currentOfferingModel) {
				throw new Error('Could not find current offering model');
			}

			const shouldUpdateTemplate = (
				selectedOfferingModel.groupid !== currentOfferingModel.groupid
				|| selectedOfferingModel.typeid !== currentOfferingModel.typeid
			);

			if (!shouldUpdateTemplate) {
				/**
				 * We are only changing to a different variant of the offering and the size stays the same,
				 * so there's no need to change the template or dimensions.
				 */
				ProductStateModule.changePage({
					id: pageModel.id,
					offeringId: selectedOfferingModel.id,
				});
			} else {
				const pageStateModel = ThemeStateModule.getVirtualPageStateData(
					selectedOfferingModel.id,
				);

				if (!pageStateModel) {
					throw new Error('Could not find virtual page state data');
				}

				const pageIndex = ProductStateModule.getPageIndex(pageModel);

				let layoutId: DB.LayoutTemplateLinkModel['layoutid'] | undefined;

				if (pageModel.template) {
					// The page has a template assigned, we try to locate the layout this template belongs to
					const linkModel = ThemeDataModule.layouttemplatelinks.find((model) => (
						model.templateid === pageModel.template
					));

					if (linkModel?.layoutid) {
						layoutId = linkModel.layoutid;
					}
				}

				if (!layoutId) {
					/*
					 * We can't find the layout from the page properties
					 * Let's try to use the project's setting
					 */
					const productModel = ProductStateModule.getProduct;

					if (productModel?.layoutid) {
						layoutId = productModel.layoutid;
					}
				}

				const themePageStateModel = ThemeStateModule.getPageStateData(
					pageIndex,
					0,
					{
						layoutId,
						photoOrientation: pageModel.orientation,
						offeringId: selectedOfferingModel.id,
					},
				);

				if (!themePageStateModel) {
					throw new Error('Could not find page state model');
				}

				const themePageModel = themePageStateModel.pageModel;
				ProductStateModule.changePage({
					id: pageModel.id,
					offeringId: selectedOfferingModel.id,
					template: themePageModel.template,
					width: themePageModel.width,
					height: themePageModel.height,
					offset: themePageModel.offset,
					customLayout: false,
				});
				ProductStateModule.resetMaskImage();
				AppStateModule.setMask(null);
				ProductState.changeTemplate(pageModel);
			}

			if (!skipHistory) {
				// Save marker in project history (for undo/redo)
				ProductStateModule.pushHistory();
			}
		}
	}

	static changeLayout(
		layoutId: number,
		isSwitch: boolean,
	): Promise<void> {
		const productModel = ProductStateModule.getProduct;

		if (!productModel) {
			return Promise.reject(new Error('Could not find product model'));
		}
		if (productModel.layoutid == layoutId) {
			if (isSwitch) {
				return Promise.resolve();
			}

			return ProductState.autoFill();
		}

		return ProductsModule
			.putModel({
				id: productModel.id,
				data: {
					layoutid: layoutId,
				},
			})
			.then(() => {
				const offeringModel = ProductStateModule.getOffering;

				if (offeringModel?.virtual) {
					// Set heavy load flag, to avoid browser freeze with following operation
					AppStateModule.setHeavyLoad();

					const closeLoader = DialogService.openLoaderDialog();

					const arrPromises: Promise<void>[] = [];
					ProductStateModule.getPages.forEach((pageModel) => {
						arrPromises.push(
							this.changeLayoutPage(
								pageModel,
								layoutId,
							),
						);
					});

					return Promise
						.allSettled(arrPromises)
						.then(() => {
							ProductStateModule.pushHistory();
						})
						.finally(() => {
							AppStateModule.unsetHeavyLoad();
							closeLoader();
						});
				}

				return ProductState.changeProjectPages(
					true,
					undefined,
					true,
				);
			});
	}

	static changeLayoutPage(
		pageModel: PI.PageModel,
		layoutId: DB.LayoutModel['id'],
	): Promise<void> {
		const pageIndex = ProductStateModule.getPageIndex(pageModel);
		const themePageStateModel = ThemeStateModule.getPageStateData(
			pageIndex,
			0,
			{
				layoutId,
				offeringId: pageModel.offeringId,
				photoOrientation: pageModel.orientation,
			},
		);

		if (themePageStateModel) {
			ProductStateModule.changePage({
				id: pageModel.id,
				template: themePageStateModel.pageModel.template,
				customLayout: false,
			});
		}

		return ProductState.changeTemplate(
			pageModel,
		);
	}

	static changeTheme(
		themeid: DB.ThemeModel['id'],
		replaceContent: boolean,
		fill: boolean,
		pushHistory?: boolean,
	): Promise<void> {
		const productModel = ProductStateModule.getProduct;

		if (!productModel) {
			return Promise.reject(
				new Error('Could not find product model'),
			);
		}
		if (productModel.themeid == themeid) {
			return Promise.resolve();
		}

		return Theme
			.setup(themeid)
			.then(() => {
				const data: Partial<DB.ProductModel> = {
					themeid,
				};

				const themeModel = ThemeDataModule.getTheme(themeid);

				if (themeModel && themeModel.layoutid) {
					data.layoutid = themeModel.layoutid;
				} else if (!productModel.layoutid || !_.findWhere(
					ThemeStateModule.layoutCollection,
					{
						id: productModel.layoutid,
					},
				)) {
					data.layoutid = null;
				}

				return ProductsModule.putModel({
					id: productModel.id,
					data,
				});
			})
			.then(() => ProductState.changeProjectPages(
				replaceContent,
				undefined,
				fill,
				pushHistory,
			));
	}

	static findBestVirtualOfferingChild(
		idealWidth: PI.PageModel['width'],
		idealHeight: PI.PageModel['height'],
		variantid?: DB.OfferingModel['variantid'],
	): DB.OfferingModel | undefined {
		// Find the best fitting child offering model
		const virtualOfferingModel = ProductStateModule.getOffering;

		if (!virtualOfferingModel) {
			throw new Error('Could not find required offering model');
		}

		const idealRatio = idealWidth / idealHeight;
		let groupOfferingModels = AppDataModule.findOffering({
			flexgroupid: virtualOfferingModel.flexgroupid,
			variantid: variantid || ProductStateModule.productSettings.childOfferingVariantId || 1,
			instock: 1,
			virtual: 0,
		});

		if (UserModule.countryid) {
			const countryModel = AppDataModule.getCountry(
				UserModule.countryid,
			);

			if (countryModel) {
				groupOfferingModels = groupOfferingModels.filter(
					(offeringModel) => !!AppDataModule.findRegionOfferingLinkWhere({
						regionid: countryModel.regionid,
						offeringid: offeringModel.id,
					}),
				);
			}
		}

		const sortedChildOfferingModels = _.sortBy(
			groupOfferingModels,
			(m) => {
				const pageWidth = idealWidth >= idealHeight
					? Math.max(
						m.width,
						m.height,
					)
					: Math.min(
						m.width,
						m.height,
					);
				const pageHeight = idealHeight > idealWidth
					? Math.max(
						m.width,
						m.height,
					)
					: Math.min(
						m.width,
						m.height,
					);

				// First sorting is based on the ideal ratio of the print
				const precisionScore = Math.abs(idealRatio - (pageWidth / pageHeight));
				/*
				 * We use the precision score setting to group almost similar scores together
				 * (small croppings are almost invisible to the user)
				 */
				const mainScore = Math.max(
					0,
					precisionScore - (1 - ConfigModule['pageSize.bestFit.precision']),
				);

				/*
				 * In case the mainScore is equal, the subScore will be used for ranking
				 * We give preference to the smallest print
				 */
				const subScore = m.width * m.height;
				const score = Math.round(mainScore * 1000) + (1 - (1 / subScore));

				return score;
			},
		);

		if (sortedChildOfferingModels.length) {
			return sortedChildOfferingModels[0];
		}

		return undefined;
	}

	static changeProjectPages(
		replaceContent: boolean,
		pageCount: number | undefined,
		fill: boolean,
		pushHistory?: boolean,
	): Promise<void> {
		const productModel = ProductStateModule.getProduct;
		const offeringModel = ProductStateModule.getOffering;

		if (!productModel) {
			throw new Error('Could not find product model');
		}
		if (!offeringModel) {
			throw new Error('Could not find offering model');
		}

		if (ProductStateModule.pageList.length) {
			if (replaceContent) {
				const serialNumbers: number[] = [];

				if (OfferingGroups(
					offeringModel.groupid,
					[
						'BasicProducts',
						'PhotoFrameBox',
					],
				)) {
					serialNumbers.push(0);
				}
				if (OfferingGroups(
					offeringModel.groupid,
					[
						'Cards',
						'PhotoFrameBox',
					],
				)) {
					serialNumbers.push(1);
				}

				if (serialNumbers.length) {
					serialNumbers.forEach((serialNumber) => {
						const pageModel = ProductStateModule.getPageByNumber(serialNumber);

						if (!pageModel) {
							throw new Error('Missing required pageModel');
						}

						const themePageStateModel = ThemeStateModule.getPageStateData(
							serialNumber,
							0,
							{
								photoOrientation: pageModel.orientation,
								offeringId: pageModel.offeringId,
							},
						);

						if (!themePageStateModel) {
							throw new Error('Could not find page state model');
						}

						// Replace smartTags
						const themePageModel = themePageStateModel.pageModel;
						const pageData: OptionalExceptFor<PI.PageModel, 'id'> = {
							id: pageModel.id,
							bgcolor: themePageModel.bgcolor,
							bgimage: themePageModel.bgimage,
							bgpattern: themePageModel.bgpattern,
							editable: themePageModel.editable,
							movable: themePageModel.movable,
							scaling: themePageModel.scaling,
							template: themePageModel.template,
							customLayout: false,
						};
						Theme.replaceTags(pageData);
						ProductStateModule.changePage(pageData);

						ProductState.changeTemplate(pageModel);
					});

					return Promise.resolve();
				}
			} else {
				// Switch templates to those of new theme
				ProductStateModule.pageList.forEach((_pageId, pageIndex) => {
					const pageModel = ProductStateModule.getPageByNumber(pageIndex);

					if (!pageModel) {
						throw new Error('Missing required pageModel');
					}

					const themePageStateModel = ThemeStateModule.getPageStateData(
						pageIndex,
						0,
						{
							offeringId: offeringModel.id,
						},
					);

					if (!themePageStateModel) {
						throw new Error('Could not find page state model');
					}

					const themePageModel = themePageStateModel.pageModel;
					const pageData: OptionalExceptFor<PI.PageModel, 'id'> = {
						id: pageModel.id,
						template: themePageModel.template,
						templateSetId: null,
					};
					ProductStateModule.changePage(pageData);
				});
			}
		}

		if (fill) {
			return Product.fill({
				navigate: false,
				showBuildOptions: false,
				replaceContent,
				pageCount,
				pushHistory,
			});
		}

		return Promise.resolve();
	}

	static changePageObjectFill(
		objectModel: PI.PageObjectModel,
		newFillMethod: PI.PageObjectModel['fillMethod'],
		angle?: number,
	): void {
		if (!objectModel.photoid) {
			throw new Error('Missing required property photoid');
		}
		if (!objectModel.templatestateid) {
			throw new Error('Missing required property templatestateid');
		}

		const pageModel = ProductStateModule.findPageFromObject(objectModel.id);

		if (!pageModel) {
			throw new Error('Could not find required page model');
		}

		const offeringModel = ProductStateModule.getOffering;
		const photoModel = PhotosModule.getById(objectModel.photoid);

		if (!photoModel) {
			throw new Error('Could not find required photo model');
		}

		const templatePositions = ProductStateModule.getPageTemplatePositions({
			pageModel,
			dpi: offeringModel?.configdpi,
		});

		const templatePositionModel = templatePositions.find((position) => (
			position.id === objectModel.templatestateid
		)) as TemplatePhotoPosition;

		if (!templatePositionModel) {
			throw new Error('Missing required template position model');
		}

		analytics.trackEvent(
			'Change Object Fill',
			{
				fillMethod: newFillMethod,
			},
		);

		const photoData: Omit<GridPhotoData, 'url'> = {
			id: photoModel.id,
			width: photoModel.full_width,
			height: photoModel.full_height,
			caption: photoModel.title || undefined,
		};

		if (
			typeof photoModel.fcx !== 'undefined'
			&& photoModel.fcx !== null
			&& typeof photoModel.fcy !== 'undefined'
			&& photoModel.fcy !== null
			&& typeof photoModel.fcw !== 'undefined'
			&& photoModel.fcw !== null
			&& typeof photoModel.fch !== 'undefined'
			&& photoModel.fch !== null
		) {
			photoData.facebox = {
				x: photoModel.fcx,
				y: photoModel.fcy,
				width: photoModel.fcw,
				height: photoModel.fch,
			};
		}

		let finalAngle = templatePositionModel.angle;

		if (typeof angle !== 'undefined') {
			finalAngle = angle;
		}

		const props = Template.fitPhotoInRectangle(
			{
				x: templatePositionModel.x,
				y: templatePositionModel.y,
				width: templatePositionModel.width,
				height: templatePositionModel.height,
				angle: finalAngle,
				borderwidth: templatePositionModel.borderwidth,
				autoRotate: Boolean(templatePositionModel.autorotate),
			},
			photoData,
			undefined,
			{
				fit: newFillMethod === 'contain',
				resizing: {
					maxScale: (
						offeringModel
							? offeringModel.configdpi / offeringModel.minimumdpi
							: 1000
					),
					recommendedMaxScale: (
						offeringModel
							? offeringModel.configdpi / offeringModel.qualitydpi
							: 1000
					),
				},
			},
		);

		ProductStateModule.changePageObject({
			id: objectModel.id,
			/*
			 * TODO: commented for now, seems to be unncecessary at the moment
			 * photoid: photoModel.id,
			 * TODO: commented for now, but confirm if should be removed or added back
			 * effect: null,
			 */
			x_axis: props.x,
			y_axis: props.y,
			width: props.width,
			height: props.height,
			cropwidth: props.cropWidth,
			cropheight: props.cropHeight,
			cropx: props.cropX,
			cropy: props.cropY,
			maxwidth: photoModel.full_width,
			maxheight: photoModel.full_height,
			fillMethod: newFillMethod,
		});

		ProductStateModule.pushHistory();
	}

	static changePageOrientation(
		pageModel: PI.PageModel,
	): void {
		const offeringModel = ProductStateModule.getOffering;

		if (!offeringModel) {
			throw new Error('Missing required offeringModel');
		}

		if (offeringModel.virtual && pageModel.offeringId) {
			analytics.trackEvent(
				'Change Page Orientation',
				{},
			);

			const newOrientation = pageModel.orientation === 'p'
				? 'l'
				: 'p';
			const pageStateModel = ThemeStateModule.getVirtualPageStateData(
				pageModel.offeringId,
				newOrientation,
			);

			if (!pageStateModel) {
				throw new Error('Could not find virtual page state data');
			}

			const pageIndex = ProductStateModule.getPageIndex(pageModel);
			const productModel = ProductStateModule.getProduct;
			const themePageStateModel = ThemeStateModule.getPageStateData(
				pageIndex,
				0,
				{
					layoutId: productModel?.layoutid || undefined,
					offeringId: pageModel.offeringId,
					photoOrientation: newOrientation,
				},
			);

			if (!themePageStateModel) {
				throw new Error('Could not find page state model');
			}

			const themePageModel = themePageStateModel.pageModel;
			ProductStateModule.changePage({
				id: pageModel.id,
				template: themePageModel.template,
				width: pageModel.height,
				height: pageModel.width,
				orientation: newOrientation,
			});

			ProductState.changeTemplate(
				pageModel,
			);

			// Save history snapshot (for undo/redo)
			ProductStateModule.pushHistory();
		} else {
			const rotatedOfferingModel = AppDataModule.findOfferingWhere({
				flexgroupid: offeringModel.flexgroupid,
				variantid: offeringModel.variantid,
				width: offeringModel.height,
				height: offeringModel.width,
			});

			if (rotatedOfferingModel) {
				analytics.trackEvent(
					'Change Offering Orientation',
					{},
				);

				AppStateModule.setHeavyLoad();
				const closeLoader = DialogService.openLoaderDialog();

				ProductState.changeOffering(rotatedOfferingModel.id)
					.then(() => {
						// Save history snapshot (for undo/redo)
						ProductStateModule.pushHistory();
					})
					.catch((e: Error) => {
						// Swallow error: no action required
						if (typeof window.glBugsnagClient !== 'undefined') {
							window.glBugsnagClient.notify(
								e,
								(event) => { event.severity = 'warning'; },
							);
						}
					})
					.finally(() => {
						closeLoader();
						AppStateModule.unsetHeavyLoad();
					});
			}
		}
	}

	/**
	 * Removes the current content of the page in the project, applies the new template,
	 * and fills the page with previous content
	 *
	 * @param pageModel The page model you want to apply the new template to
	 * @param templateSetId The id of this combination of template positions (calculated as a hash of position properties)
	 * @param opts Configuration options to use
	 * @param opts.fit Use the "fit" method (instead of "fill") to make sure photos will be fully visible (no cropping)
	 * @param opts.objectTypes The type of objects to remove from the page before applying the new template
	 * @param opts.photoModels An array of photo models that should be added additionally to the page
	 */
	static changeTemplate(
		pageModel: PI.PageModel,
		templateSetId?: string,
		opts?: {
			fit?: boolean;
			objectTypes?: PI.PageObjectModel['type'][];
			photoModels?: PI.PhotoModel[];
		},
	): Promise<void> {
		const fit = !!opts?.fit;
		const objectTypes: PI.PageObjectModel['type'][] = opts?.objectTypes || [
			'photo',
			'text',
		];

		// Store all photos that are currently displayed on the page in an array for recyling
		let photoModels: PI.PhotoModel[] = [...opts?.photoModels || []];
		const pageTextObjectModels: PI.PageObjectModel[] = [];

		ProductStateModule
			.getPageObjects(pageModel)
			.forEach((objectModel) => {
				if (objectModel.type === 'text') {
					pageTextObjectModels.push(
						JSON.parse(JSON.stringify(objectModel)),
					);
				} else if (objectModel.photoid) {
					const photoModel = PhotosModule.getById(objectModel.photoid);

					if (photoModel) {
						photoModels.push(photoModel);
					}
				}
			});

		const offeringModel = ProductStateModule.getOffering;

		if (
			offeringModel
			&& offeringModel.maxpages <= 2
		) {
			photoModels = ProductStateModule.getPhotosSelected;
		}

		// Remove all existing page objects
		return Page.deleteObjects(
			pageModel,
			{
				objectTypes,
			},
		).finally(() => {
			// Add theme objects to the page
			const pageIndex = ProductStateModule.getPageIndex(pageModel);
			const themePageStateData = ThemeStateModule.getPageStateData(
				pageIndex,
				pageIndex,
				{
					offeringId: pageModel.offeringId,
					photoOrientation: pageModel.orientation,
				},
			);

			if (themePageStateData) {
				Page.copyObjects(
					themePageStateData,
					pageModel,
				);
			}

			if (pageModel.template
				&& photoModels.length
			) {
				// Get the template positions we need to fill
				const templateSet = templateSetId
					? ThemeStateModule.getTemplateSetById(
						pageModel.template,
						templateSetId,
						{
							marginAroundEdge: pageModel.templateMarginAroundEdge,
							marginBetweenPositions: pageModel.templateMarginBetweenPositions,
						},
					)
					: ThemeStateModule.getTemplateSet(
						pageModel.template,
						photoModels,
					).templateSet;

				if (templateSet) {
					// Fill template positions
					return template
						.fillTemplateSet(
							pageModel,
							templateSet,
							photoModels,
							{
								fit,
							},
						)
						.catch(() => {
							/*
							 * Swallow error: no action required
							 * For instance: the promise is rejected when there are no photo positions
							 * in the new template set, but there are photoModels available
							 */
						})
						.then(() => {
							ProductStateModule.changePage({
								id: pageModel.id,
								templateSetId,
							});
						});
				}
			}

			if (templateSetId) {
				ProductStateModule.changePage({
					id: pageModel.id,
					templateSetId,
				});
			}

			return Promise.resolve();
		}).then(() => {
			// Try to re-add text object to page
			while (
				ProductStateModule
					.getPageTemplatePositionsAvailable({
						pageModel,
						dpi: offeringModel?.configdpi,
					})
					.filter((templatePosition) => templatePosition.type == 'text').length
				&& pageTextObjectModels.length
			) {
				const textPosition = ProductStateModule
					.getPageTemplatePositionsAvailable({
						pageModel,
						dpi: offeringModel?.configdpi,
					})
					.filter((templatePosition) => templatePosition.type == 'text')[0] as TemplateTextPosition;
				const textObjectModel = pageTextObjectModels.shift();

				TemplatePosition.fillTextPosition(
					pageModel,
					textPosition,
					textObjectModel?.text || '',
					{
						force: true,
					},
				);
			}
		});
	}

	// Finalize is called when the product needs to be saved to the server before the user can continue
	public static finalize(
		showCancel?: boolean,
		showRemove?: boolean,
	): Promise<boolean> {
		if (!ProductStateModule.productId) {
			// There is no product to save, continue
			return Promise.resolve(true);
		}

		if (ProductStateModule.getSaved) {
			const productModel = ProductStateModule.getProduct;

			if (!productModel?.read_token) {
				return Promise.resolve(true);
			}

			// Remove from local storage, we have a full copy saved to the server
			return ProductState
				.removeFromStorage(productModel.read_token)
				.catch(() => {
					// Swallow error: no action required
				})
				.then(() => true);
		}

		if (!AppStateModule.online) {
			/*
			 * We are offline, so wait for user to come back online
			 * Show offline message to user
			 */
			return new Promise<boolean>((resolve, reject) => {
				const closeError = DialogService.openErrorDialog({
					header: {
						hasCloseButton: false,
						title: window.App.router.$t('dialogHeaderOffline'),
					},
					body: {
						content: window.App.router.$t('dialogTextOffline'),
					},
					footer: {
						buttons: [
							{
								id: 'cancel',
								text: window.App.router.$t('dialogButtonCancel'),
								click: () => {
									reject();
									closeError();
								},
							},
							{
								id: 'accept',
								text: window.App.router.$t('dialogButtonOfflineOk'),
								click: () => {
									const closeLoader = DialogService.openLoaderDialog();

									// Try ping to check if we are back online
									checkConnection()
										.catch(() => {
											// Swallow error: no action required
										})
										.then(() => {
											closeLoader();

											ProductState
												.finalize(
													showCancel,
													showRemove,
												).then(
													resolve,
													reject,
												);
										});

									closeError();
								},
							},
						],
					},
				});
			});
		}

		// Temporarily disable auto saving
		AppStateModule.disableAutoSave();

		// Save product to database
		return ProductState
			.finalizeUploads({
				showCancel,
				showRemove,
				dialogHeader: window.App.router.$t('dialogTextSave'),
			})
			.then((saved) => {
				if (!saved) {
					return false;
				}

				return new Promise<boolean>((resolve, reject) => {
					UIModule.openDialog({
						body: dialogSlot({
							tag: SavingProjectView,
							props: {
								hasCancel: showCancel,
								onCancel: () => {
									UIModule.closeDialog();
									resolve(false);
								},
							},
						}),
						props: {
							class: 'my-0 w-62.5',
							hasCloseButton: false,
						},
					});

					// Start saving product to server
					ProductState
						.save()
						.then(() => {
							const productModel = ProductStateModule.getProduct;

							if (!productModel) {
								return true;
							}

							// Remove from local storage, we have a full copy saved to the server
							return ProductState
								.removeFromStorage(productModel.read_token)
								.catch(() => {
									// Swallow error: no action required
								})
								.then(() => true);
						})
						.then(() => {
							UIModule.closeDialog();

							// Successfully saved to database
							resolve(true);
						})
						.catch((error) => {
							UIModule.closeDialog();

							// Failed saving to database
							reject(error);
						});
				});
			})
			.then((saved) => {
				// Enable auto saving again
				AppStateModule.enableAutoSave();

				return saved;
			})
			.catch((error) => {
				// Enable auto saving again
				AppStateModule.enableAutoSave();

				throw error;
			});
	}

	/**
	 * Save the product state to database.
	 *
	 * Saves only happen one at a time, if a save is already in progress,
	 * the new save will be queued and executed after the current save is finished.
	 * @param bypassQueue Flag that if true the save logic will be executed immediately
	 * even if there is a save in progress, this is intended to be used when the same
	 * function is being called recursively, for example when a save fails and we need
	 * to retry the save.
	 * @returns Promise that resolves when the save is finished or rejects if an error occurs
	 */
	public static save(bypassQueue = false): Promise<void> {
		/**
		 * If we are already saving, we wait for the current save to finish
		 * before starting a new one, unless we are bypassing the queue
		 * which is happening when the same function is being called
		 * recursively.
		 */
		if (
			ProductState.savePromise
			&& !bypassQueue
		) {
			ProductState.savePromise = ProductState.savePromise.finally(() => ProductState.save());

			return ProductState.savePromise;
		}

		ProductState.savePromise = new Promise<void>((saveResolve, saveReject) => {
			// Check if we can save to database
			if (
				AppStateModule.online
				&& !AppStateModule.sync
			) {
				// Online and no save in progress, so start saving to database
				ProductState
					.saveToDatabase(
						500,
						true,
					)
					.then(() => {
						if (ProductStateModule.getPhotosFailed.length) {
							if (AppStateModule.showUploadErrorDialog) {
								return new Promise<void>((resolve, reject) => {
									const closeError = DialogService.openErrorDialog({
										header: {
											hasCloseButton: false,
										},
										body: {
											component: UploadErrorsView,
										},
										footer: {
											buttons: [
												{
													id: 'abort',
													text: window.App.router.$t('dialogButtonUploadErrorsCancel'),
													click: () => {
														closeError();

														if (ProductStateModule.getPhotosFailed.length) {
															return ProductStateModule
																.retryPhotoErrors()
																.catch(() => {
																	// Swallow error: no action required
																})
																.finally(() => {
																	ProductState
																		.save(true) // Set the bypassQueue flag to true to bypass the queue
																		.then(resolve)
																		.catch(reject);
																});
														}

														return ProductState
															.save(true) // Set the bypassQueue flag to true to bypass the queue
															.then(resolve)
															.catch(reject);
													},
												},
												{
													id: 'accept',
													text: window.App.router.$t('dialogButtonUploadErrorsOk'),
													click: () => {
														closeError();

														const errorModels = ProductStateModule.getPhotosFailed;
														errorModels
															.slice(0)
															.forEach((photoModel) => ProductStateModule.removePhoto(photoModel.id));

														ProductState
															.save(true) // Set the bypassQueue flag to true to bypass the queue
															.then(resolve)
															.catch(reject);
													},
												},
											],
										},
									});
								});
							}

							throw new Error('Unsaved photo models');
						}
						if (
							!AppStateModule.sync
							&& ProductStateModule.getSaved
						) {
							EventBus.emit('product:save:finished');
							return undefined;
						}

						return new Promise<void>((resolve, reject) => {
							window.setTimeout(
								() => {
									ProductState
										.save(true) // Set the bypassQueue flag to true to bypass the queue
										.then(resolve)
										.catch(reject);
								},
								500,
							);
						});
					})
					.catch((error) => {
						EventBus.emit(
							'product:save:finished',
							error,
						);
						throw error;
					})
					.then(saveResolve)
					.catch(saveReject);
				return;
			}
			if (AppStateModule.online) {
				// Save in progress, so delay process
				window.setTimeout(
					() => {
						ProductState
							.save(true) // Set the bypassQueue flag to true to bypass the queue
							.then(saveResolve)
							.catch(saveReject);
					},
					500,
				);
				return;
			}

			const error = new Error(ERRORS_OFFLINE);
			EventBus.emit(
				'product:save:finished',
				error,
			);

			saveReject(error);
		}).finally(() => {
			/**
			 * Reset the savePromise property back to null to allow new saves to run
			 * without putting them in a queue.
			 */
			ProductState.savePromise = null;
		});

		return ProductState.savePromise;
	}

	static saveToDatabase(
		delay = 500,
		retry = false,
	): Promise<void> {
		if (AppStateModule.sync) {
			throw new Error('Save already in progress');
		}

		if (!ProductStateModule.productId) {
			return Promise.resolve();
		}

		if (!AppStateModule.online) {
			throw new Error(ERRORS_OFFLINE);
		}

		if (!retry
			&& UploadModule.error.length
			&& UploadModule.error.filter((uploadModel) => uploadModel.errorCount >= 3).length
		) {
			throw new Error('Upload failure');
		}

		if (UploadModule.error.length) {
			upload.retryAll();

			return ProductState.saveToDatabase(delay);
		}

		if (UploadModule.getInProgress.length) {
			// Wait for running processes to finish
			return new Promise((resolve, reject) => {
				window.setTimeout(
					() => {
						ProductState.saveToDatabase(delay).then(
							resolve,
							reject,
						);
					},
					delay,
				);
			});
		}

		if (ProductStateModule.getPhotosQueued.length) {
			const stuckModels = ProductStateModule.getPhotosQueued.filter(
				(photoModel) => !photoModel._processing,
			);

			if (stuckModels.length) {
				stuckModels.filter(
					(stuckModel) => stuckModel.full_url,
				).forEach((stuckModel) => {
					PhotosModule.retryModel(stuckModel.id);
				});
			}

			// Wait for running processes to finish
			return new Promise((resolve, reject) => {
				window.setTimeout(
					() => {
						ProductState.saveToDatabase(delay).then(
							resolve,
							reject,
						);
					},
					delay,
				);
			});
		}

		if (ProductStateModule.getSaved) {
			return Promise.resolve();
		}

		AppStateModule.setSync(true);

		return ProductStateModule.save()
			.then(() => {
				AppStateModule.setSync(false);
				return undefined;
			}).catch((err) => {
				AppStateModule.setSync(false);
				throw err;
			});
	}

	/**
	 * Get the margin options to place the photos in the book
	 * Used for the slider in the dynamic fill dialog
	 * @param offeringModel - The offering model of the product to fill
	 *
	 * @returns array with margin options
	 */
	public static getDynamicFillMarginOptions(
		offeringModel: DB.OfferingModel,
	): EditorBookFillMargins {
		const safeMargin = offeringModel.pagemargin || 40;
		return [
			{
				id: 'large',
				value: 2 * safeMargin,
			},
			{
				id: 'small',
				value: safeMargin,
			},
			{
				id: 'zero',
				value: -offeringModel.bleedmargin,
			},
		];
	}

	/**
	 * Get the orientation option to place the photos in the book
	 * Used for the slider in the dynamic fill dialog
	 * @param offeringModel - The offering model of the product to fill
	 * @returns orientation option
	 */
	public static getDynamicFillOrientation(
		offeringModel: DB.OfferingModel,
	): EditorBookFillImagePageOrientation {
		let orientation: EditorBookFillImagePageOrientation = 'square';

		if (offeringModel.width * 0.98 > offeringModel.height) {
			orientation = 'landscape';
		} else if (offeringModel.width < offeringModel.height * 0.98) {
			orientation = 'portrait';
		}

		return orientation;
	}

	/**
	 * Calculate the number of pages required to place the photos in the product
	 * Used for the slider in the dynamic fill dialog
	 * @param densityId - The density id of the product to fill
	 * @param numberOfPhotos - Number of photos to place in the book
	 * @param offeringModel - The offering model of the product to fill
	 * @param baseOption - The base option of the page count and price to compare
	 * with for marking the base option in the UI, this is optional as core logic
	 * doesn't require it, it's only used for UI purposes
	 * @returns Number of pages to fill
	 */
	public static getDynamicFillPageOption(
		densityId: EditorBookFillPage['id'],
		numberOfPhotos: number,
		offeringModel: DB.OfferingModel,
		baseOption?: DynamicFillBaseOptionPricing,
	): EditorBookFillPage {
		const isBook = OfferingGroups(
			offeringModel.groupid,
			['BookTypes'],
		);

		let averageNumberOfPhotosPerPage = 1;

		if (densityId === 'highest') {
			averageNumberOfPhotosPerPage = 4.5;
		}
		if (densityId === 'high') {
			averageNumberOfPhotosPerPage = 3;
		}
		if (densityId === 'moderate') {
			averageNumberOfPhotosPerPage = 2;
		}
		if (densityId === 'fewer') {
			averageNumberOfPhotosPerPage = 1.5;
		}
		if (densityId === 'single') {
			averageNumberOfPhotosPerPage = 1;
		}

		// Calculate the number of pages required to fill all photos
		const pagesToFill = Math.ceil(numberOfPhotos / averageNumberOfPhotosPerPage);
		const backendOnlyFixedPagesCount = ProductStateModule.getBackendOnlyFixedPagesCount;

		let totalProjectPages = (
			isBook
				? pagesToFill + 2 + backendOnlyFixedPagesCount // Add covers and backend-only fixed pages
				: pagesToFill
		);

		// Make sure the number of pages is a multiple of the page interval of the offering
		totalProjectPages += (totalProjectPages % offeringModel.pageinterval);

		if (totalProjectPages > offeringModel.maxpages) {
			// We cannot fill more pages than the maximum number of pages in the offering
			totalProjectPages = offeringModel.maxpages;
		}

		if (totalProjectPages < offeringModel.minprintpages) {
			// We cannot fill less pages than the minimum number of pages in the offering
			totalProjectPages = offeringModel.minprintpages;
		}

		// Calculate the price for this number of pages
		const price: number = PriceCalculator.productPrice(
			offeringModel,
			totalProjectPages,
			{
				quantity: 1,
			},
		).subTotal / 100;

		const innerPagesAfterAutoFill = (
			isBook
				? totalProjectPages - 2 // Substract 2 for the cover pages
				: totalProjectPages
		);
		const autoFillPageCount = Math.min(
			pagesToFill,
			offeringModel.maxpages,
		);

		return {
			id: densityId,
			averageNumberOfPhotosPerPage,
			innerPagesAfterAutoFill,
			price,
			autoFillPageCount,
			isBaseOption: (
				baseOption
					? innerPagesAfterAutoFill === baseOption.basePageCount
					: false
			),
			basePageCount: (
				baseOption?.basePageCount
				?? 0
			),
			basePrice: (
				baseOption?.basePrice
				?? 0
			),
			extraPagesPrice: Math.max(
				price - (baseOption?.basePrice ?? 0),
				0,
			),
		};
	}

	private static getDynamicFillBaseOptionPricing(
		offeringModel: DB.OfferingModel,
		pageCount: number,
	): DynamicFillBaseOptionPricing {
		const basePageCount = pageCount;
		let totalProjectPages = pageCount + 2;

		if (totalProjectPages < offeringModel.minprintpages) {
			totalProjectPages = offeringModel.minprintpages;
		}

		if (totalProjectPages > offeringModel.maxpages) {
			totalProjectPages = offeringModel.maxpages;
		}

		return {
			basePageCount,
			basePrice: PriceCalculator.productPrice(
				offeringModel,
				totalProjectPages,
				{
					quantity: 1,
				},
			).subTotal / 100,
		};
	}

	/**
	 * Get the page options to place the photos in the book
	 * Used for the slider in the dynamic fill dialog
	 * @param numberOfPhotos - The number of photos to place in the project
	 * @param offeringModel - The offering model of the product to fill
	 * @returns array with page options
	 */
	public static getDynamicFillPageOptions(
		numberOfPhotos: number,
		offeringModel: DB.OfferingModel,
	): EditorBookFillPages {
		const baseOption = this.getDynamicFillBaseOptionPricing(
			offeringModel,
			offeringModel.minprintpages - 2,
		);

		return [
			this.getDynamicFillPageOption(
				'highest',
				numberOfPhotos,
				offeringModel,
				baseOption,
			),
			this.getDynamicFillPageOption(
				'high',
				numberOfPhotos,
				offeringModel,
				baseOption,
			),
			this.getDynamicFillPageOption(
				'moderate',
				numberOfPhotos,
				offeringModel,
				baseOption,
			),
			this.getDynamicFillPageOption(
				'fewer',
				numberOfPhotos,
				offeringModel,
				baseOption,
			),
			this.getDynamicFillPageOption(
				'single',
				numberOfPhotos,
				offeringModel,
				baseOption,
			),
		];
	}

	static saveBuildConfiguration(
		config: {
			autoFill: boolean;
			pushHistory?: boolean;
		},
	) {
		const productModel = ProductStateModule.getProduct;

		if (!productModel) {
			throw new Error('Could not find required product model');
		} else {
			ProductStateModule.changeProductSettings({
				autoFill: config.autoFill,
			});

			if (
				OfferingGroups(
					productModel.group,
					['BuildOptions'],
				)
			) {
				const offeringModel = ProductStateModule.getOffering;

				if (!offeringModel) {
					throw new Error('Could not find required offeringModel');
				}

				const autoFill = () => {
					if (
						ThemeStateModule.themeHasDynamicLayout
						&& offeringModel.minpages < offeringModel.maxpages
					) {
						const pageOptions = this.getDynamicFillPageOptions(
							ProductStateModule.getPhotosSelected.length,
							offeringModel,
						);
						const marginOptions = this.getDynamicFillMarginOptions(
							offeringModel,
						);
						const orientationOption = this.getDynamicFillOrientation(
							offeringModel,
						);

						UIModule.openDialog({
							body: dialogSlot({
								tag: EditorBookFillView,
								props: {
									currencyModel: (
										UserModule.currency
											? AppDataModule.getCurrency(UserModule.currency)
											: AppDataModule.defaultCurrency
									)!,
									margins: marginOptions,
									orientation: orientationOption,
									pages: pageOptions,
									productLabel: AppDataModule.productGroupName(offeringModel.groupid),
									value: {
										margins: ProductStateModule.getProductSettings.autoFillMarginId ?? marginOptions[1].id,
										pages: ProductStateModule.getProductSettings.autoFillPageDensityId ?? pageOptions[3].id,
									},
									onClose: UIModule.closeDialog,
									'onUpdate:value': (data) => {
										const selectedMarginOption = marginOptions.find(
											(option) => option.id == data.margins,
										);
										const selectedPageOption = pageOptions.find(
											(option) => option.id == data.pages,
										);

										ProductStateModule.changeProductSettings({
											autoFillPageDensityId: selectedPageOption?.id,
											autoFillMarginId: selectedMarginOption?.id,
											templateMarginAroundEdge: (selectedMarginOption?.value ?? 0),
											templateMarginBetweenPositions: Math.max(
												1,
												(selectedMarginOption?.value ?? 0) / 2,
											),
										});

										if (selectedMarginOption) {
											// Set margin style to pages that are already setup in project
											ProductStateModule.getPages.forEach((pageModel) => {
												ProductStateModule.changePage({
													id: pageModel.id,
													templateMarginAroundEdge: ProductStateModule.getProductSettings.templateMarginAroundEdge,
													templateMarginBetweenPositions: ProductStateModule.getProductSettings.templateMarginBetweenPositions,
												});
											});
										}

										/**
										 * Close the dialog with the margin and pages selection
										 */
										UIModule.closeDialog();

										/**
										 * Open a loader dialog
										 */
										const closeLoader = DialogService.openLoaderDialog();

										/**
										 * Debounce to prevent a screen freeze due to an occupied processor
										 */
										_.debounce(
											() => {
												AppStateModule.setHeavyLoad();

												ProductState
													.autoFill(
														selectedPageOption?.autoFillPageCount,
														(
															typeof config.pushHistory !== 'undefined'
																? {
																	pushHistory: config.pushHistory,
																}
																: undefined
														),
													)
													.finally(() => {
														AppStateModule.unsetHeavyLoad();
														closeLoader();
													});
											},
											100,
										)();
									},
								},
							}),
							props: {
								class: 'my-0 w-275',
								hasCloseButton: false,
								'onClick:outside': UIModule.closeDialog,
							},
						});
					} else if (ThemeStateModule.themeHasLayoutSelect) {
						UIModule.openDialog({
							header: {
								props: {
									class: 'picker',
								},
								slot: window.App.router.$t('pickerLayout'),
							},
							body: {
								tag: LayoutPickerView,
								props: {
									isSwitch: false,
									onCloseDialog: UIModule.closeDialog,
								},
							},
							props: {
								class: 'w-125',
								'onClick:outside': UIModule.closeDialog,
							},
						});
					} else {
						ProductState.autoFill();
					}
				};

				const noAutoFill = () => {
					const pageMargin = offeringModel.pagemargin ?? 0;
					ProductStateModule.changeProductSettings({
						templateMarginAroundEdge: pageMargin,
						templateMarginBetweenPositions: Math.max(
							1,
							pageMargin / 2,
						),
					});

					// Set margin style to pages that are already setup in project
					ProductStateModule.getPages.forEach((pageModel) => {
						ProductStateModule.changePage({
							id: pageModel.id,
							templateMarginAroundEdge: pageMargin,
							templateMarginBetweenPositions: pageMargin / 2,
						});
					});

					template.fillProductAttributes();
					ProductState.save();
				};

				const extraPageQuantity = offeringModel.minpages - ProductStateModule.getPageCountForLimitChecks;

				if (extraPageQuantity > 0) {
					const afterAdding = _.after(
						extraPageQuantity,
						() => {
							if (config.autoFill) {
								autoFill();
							} else {
								noAutoFill();
							}
						},
					);

					for (let y = 0; y < extraPageQuantity; y += 1) {
						ProductState
							.addPage({
								autoCompensate: false,
							})
							.then(() => {
								afterAdding();
							});
					}
				} else if (config.autoFill) {
					autoFill();
				} else {
					noAutoFill();
				}

				navigate.toOverview(productModel.id);
			}
		}
	}

	static removeFromStorage(token: string) {
		if (!storage.enabled.data) {
			return Promise.reject(new Error('No storage enabled'));
		}

		return storage.clear([
			'productData',
			token,
		]);
	}

	static finalizeUploads(options: {
		showCancel?: boolean;
		showRemove?: boolean;
		dialogHeader: string;
	}): Promise<boolean> {
		if (ProductStateModule.getPhotosQueued.length === 0) {
			return Promise.resolve(true);
		}

		return new Promise((resolve) => {
			let interval = 0;
			let closeProgress: () => void = () => {};
			const progressDialogButtons: DialogServiceButton[] = [];

			if (options.showRemove) {
				progressDialogButtons.push({
					id: 'delete',
					text: window.App.router.$t('buttonAbortUploadDestroyProject'),
					click: () => {
						// Save analytics event
						analytics.trackEvent(
							'Delete product',
							{
								category: 'Save dialog',
							},
						);

						// Show confirmation dialog
						const closeConfirm = DialogService.openConfirmDialog({
							header: {
								title: window.App.router.$t('dialogHeaderRemoveProduct'),
							},
							body: {
								content: window.App.router.$t('dialogTextRemoveProduct'),
							},
							footer: {
								buttons: [
									{
										id: 'cancel',
										text: window.App.router.$t('dialogButtonCancel'),
										click: () => {
											if (interval) {
												window.clearInterval(interval);
											}

											resolve(false);
											closeConfirm();
										},
									},
									{
										id: 'accept',
										text: window.App.router.$t('dialogButtonRemoveProductOk'),
										click: () => {
											if (ProductStateModule.getProduct) {
												deleteProduct(ProductStateModule.getProduct.id)
													.then(() => {
														navigate.toStart();
														closeConfirm();
													})
													.catch(() => {
														window.App.router.reload();
													});
											} else {
												navigate.toStart();
												closeConfirm();
											}
										},
									},
								],
							},
						});
						closeProgress();
					},
				});
			}
			if (options.showCancel) {
				progressDialogButtons.push({
					id: 'cancel',
					text: window.App.router.$t('buttonHideUploadProgress'),
					click: () => {
						if (interval) {
							window.clearInterval(interval);
						}

						resolve(false);
						closeProgress();
					},
				});
			}

			// Show dialog to indicate saving progress to user
			const progressDialog = DialogService.openProgressDialog({
				header: {
					title: options.dialogHeader,
				},
				body: {
					props: {
						value: 0,
						total: 1,
					},
				},
				footer: {
					buttons: (
						progressDialogButtons.length
							? progressDialogButtons
							: undefined
					),
				},
			});
			const apiProgress = progressDialog.api;
			closeProgress = progressDialog.close;

			// Update progress dialog
			interval = window.setInterval(
				() => {
					const dialogProgressComponent = apiProgress.bodyComponent()!;
					dialogProgressComponent.value = upload.getUploadProgressPercentage / 100;

					if (ProductStateModule.getPhotosQueued.length === 0) {
						window.clearInterval(interval);
						closeProgress();

						resolve(true);
					} else if (
						ProductStateModule.getPhotosQueued.length
						&& AppStateModule.online
						&& !AppStateModule.sync
					) {
						// Save process seems to be stuck so give it a kickstart
						ProductState.save();
					}
				},
				100,
			);
		});
	}

	static async clone(
		origin: 'ShelfItem' | 'ProductReady' | 'CartItem' | 'ProductConfirm',
	): Promise<void> {
		// Get the data of the original product
		const productData = ProductStateModule.getData;
		const productModel = ProductStateModule.getProduct;

		if (!productModel) {
			throw new Error('Could not find product model');
		}

		const productVersion = moment(productModel.lastupdate).format('X');
		const productImage = `${ConfigModule.projectImageBaseUrl}/${productModel.id}/${productModel.read_token}/${productVersion}`;
		const startTime = new Date().getTime();

		// Open dialog showing clone animation
		const { close: closeDialog } = DialogService.openDialog({
			header: {
				hasCloseButton: false,
				title: window.App.router.$t('dialogHeaderLoad'),
			},
			body: {
				component: CloneView,
				props: {
					productImage,
				},
			},
			width: 500,
		});

		// Temporarily disable auto save
		AppStateModule.disableAutoSave();

		try {
			const data: Partial<DB.ProductModel> = {
				affiliateid: productModel.affiliateid,
				group: productModel.group,
				title: productModel.title,
				typeid: productModel.typeid,
				variantid: productModel.variantid,
				themeid: productModel.themeid,
				layoutid: productModel.layoutid,
				origin: `cloneFrom${origin}`,
			};

			// Create the project record in the database
			await this.create(data);

			// Remove all pages that are setup for product by default
			while (ProductStateModule.getPages.length) {
				const pageModel = _.first(ProductStateModule.getPages);

				if (pageModel) {
					await ProductStateModule.removePage({
						pageId: pageModel.id,
					});
				}
			}
			if (ProductStateModule.getSpine) {
				await ProductStateModule.removeSpine();
			}

			// Add photos to new product
			productData.photoList.forEach((photoId) => {
				ProductStateModule.addPhoto({
					photoId,
				});
			});

			/*
			 * Clone product settings
			 * Note: important to do this before cloning the pages, otherwise the page background color will be overwritten
			 */
			ProductStateModule.changeProductSettings(productData.productSettings);

			// Clone product attributes
			productData.productAttributes.forEach((productAttribute) => {
				ProductStateModule.addAttribute({
					data: productAttribute,
				});
			});

			// Clone pages
			for (const [
				index,
				pageId,
			] of productData.pageList.entries()) {
				await this.clonePage(
					productData,
					pageId,
					index,
				);
			}

			await this.cloneSpine(productData);

			// Clone address
			/*
			 * if (productData.address) {
			 * ProductStateModule.addAddress({
			 * data: productData.address,
			 * });
			 * }
			 */
		} catch (e) {
			closeDialog();

			// Re-enable auto save
			AppStateModule.enableAutoSave();

			throw e;
		}

		/*
		 * Clone ready
		 * We want the dialog to be visible for at least 3 seconds, so user understands what's happening
		 */
		const endTime = new Date().getTime();
		const timeout = Math.max(
			0,
			3000 - (endTime - startTime),
		);

		return new Promise((resolve, reject) => {
			window.setTimeout(
				() => {
					this.save().then(
						() => {
							closeDialog();

							// Re-enable auto save
							AppStateModule.enableAutoSave();

							resolve();
						},
						reject,
					);
				},
				timeout,
			);
		});
	}

	static async clonePage(
		productData: PI.ProductDataModel,
		pageId: string,
		index: number,
	): Promise<void> {
		const pageData = productData.pages[pageId];
		const newPageData: RequiredExceptFor<PI.PageModel, 'id'> = {
			...pageData,
		};
		delete newPageData.id;
		delete newPageData.objectList;
		const newPageModel = await ProductStateModule.addPage({
			data: newPageData,
			at: index,
		});

		if (pageData.objectList) {
			for (const objectId of pageData.objectList) {
				const objectData = productData.objects[objectId];

				if (objectData?.type) {
					const newObjectData: RequiredExceptFor<PI.PageObjectModel, 'id'> = {
						...objectData,
					};
					delete newObjectData.id;

					await ProductStateModule.addPageObject({
						pageId: newPageModel.id,
						data: newObjectData,
					});
				}
			}
		}
	}

	/**
	 * Clones the project's spine page and its objects into the active product.
	 *
	 * @param productData Source {@link PI.ProductDataModel} containing spine data.
	 * @returns Promise resolved once the spine and its objects are cloned.
	 */
	public static async cloneSpine(productData: PI.ProductDataModel): Promise<void> {
		const spineData = productData.spine;

		if (!spineData) {
			return Promise.resolve();
		}

		const newSpineData: RequiredExceptFor<PI.PageModel, 'id'> = {
			...spineData,
		};
		delete newSpineData.id;
		delete newSpineData.objectList;
		const newSpineModel = await ProductStateModule.addSpine(newSpineData);

		if (spineData.objectList) {
			for (const objectId of spineData.objectList) {
				const objectData = productData.objects[objectId];

				if (objectData?.type) {
					const newObjectData: RequiredExceptFor<PI.PageObjectModel, 'id'> = {
						...objectData,
					};
					delete newObjectData.id;

					await ProductStateModule.addPageObject({
						pageId: newSpineModel.id,
						data: newObjectData,
					});
				}
			}
		}

		return Promise.resolve();
	}

	static addProductProperties(
		options: {
			skipVirtualOfferingSelect?: boolean;
		} = {},
	): Promise<void> {
		return new Promise((resolve) => {
			const productModel = ProductStateModule.getProduct;

			if (!productModel) {
				throw new Error('Could not find product model');
			}

			const offeringModel = ProductStateModule.getOffering;

			if (!offeringModel) {
				throw new Error('Could not find offering model');
			}

			const { themeModel } = ThemeStateModule;

			if (
				offeringModel.virtual
				&& !options.skipVirtualOfferingSelect
				&& ProductStateModule.getUnusedPhotos.length
			) {
				const offeringModels = AppDataModule.findOffering({
					flexgroupid: offeringModel.flexgroupid,
				});

				if (offeringModels.length === 0) {
					throw new Error('There are no offeringModels linked to this virtual one');
				}

				const { close: closeDialog } = DialogService.openDialog({
					header: {
						hasCloseButton: false,
					},
					body: {
						component: VirtualOfferingSelectView,
						props: {
							count: ProductStateModule.getUnusedPhotos.length,
							modus: 'select',
							offeringModel,
							preSelectedOfferingId: ProductStateModule.getChildOfferingId,
							regionId: UserModule.countryid
								? AppDataModule.getCountry(UserModule.countryid)?.regionid
								: 0,
							selectSize: true,
							selectFinish: true,
							selectLayout: Boolean(themeModel?.layoutselect),
						},
						listeners: {
							closeDialog: () => {
								closeDialog();
							},
						},
						styles: {
							padding: '0',
						},
					},
					listeners: {
						close: () => {
							this
								.addProductProperties({
									...options,
									skipVirtualOfferingSelect: true,
								})
								.then(resolve);
						},
					},
					width: 800,
				});
			} else {
				const arrThemeVariants = ThemeDataModule.whereVariant({
					themeid: productModel.themeid,
					input: 1,
				});

				if (arrThemeVariants.length > ProductStateModule.getAttributes.length) {
					const launchSetting = OfferingLaunchers(offeringModel.id);

					if (
						launchSetting
						&& launchSetting.before
					) {
						navigate.go(
							`launch/${offeringModel.id}`,
							{
								trigger: true,
								replace: true,
							},
						);
					} else if (
						launchSetting
						&& productModel.themeid
						&& ProductStateModule.productId
					) {
						navigate.go(
							`${ProductStateModule.productId}/launch`,
							{
								trigger: true,
								replace: true,
							},
						);
					} else {
						const { close: closeDialog } = DialogService.openDialog({
							header: {
								hasCloseButton: false,
								title: window.App.router.$t('dialogHeaderProductAttributes'),
							},
							body: {
								component: ProductAttributesView,
								listeners: {
									closeDialog: () => {
										closeDialog();
									},
								},
							},
							listeners: {
								close: () => {
									resolve();
								},
							},
						});
					}
				} else {
					resolve();
				}
			}
		});
	}

	public static async addSpine(): Promise<PI.PageModel> {
		const productModel = ProductStateModule.getProduct;
		const serialnumber = ProductStateModule.getPages.length;
		const offeringModel = ProductStateModule.getOffering;

		if (!productModel) {
			throw new Error('Could not find required product model');
		}
		if (!offeringModel) {
			throw new Error('Could not find required offering model');
		}
		if (!offeringModel.spine?.length) {
			throw new Error('Offering does not support spine');
		}

		const themePageStateData = ThemeStateModule.getPageStateData(
			serialnumber,
			serialnumber,
			{
				layoutId: productModel.layoutid || undefined,
				offeringId: undefined,
				photoOrientation: undefined,
			},
		);

		if (!themePageStateData) {
			throw new Error('Could not setup required theme page state model');
		}

		const pageData: Partial<PI.PageModel> = themePageStateData.pageModel;
		delete pageData.id;
		delete pageData.name;
		delete pageData.productid;
		delete pageData.serialnumber;

		pageData.width = projectTools.getSpineModel(
			offeringModel,
			serialnumber,
		).width;

		if (ProductStateModule.productId) {
			pageData.productid = ProductStateModule.productId;
		}

		const productSettings = ProductStateModule.getProductSettings;

		if (
			pageData.bgcolor != 'transparent'
			&& (
				productSettings.bgcolor
				|| productSettings.bgpattern
			)
		) {
			pageData.bgpattern = productSettings.bgpattern;
			pageData.bgcolor = productSettings.bgcolor;
		}

		// Replace smartTags
		Theme.replaceTags(pageData);

		return ProductStateModule.addSpine(pageData);
	}

	public static addPage(options: ProductStateAddPageOptions): Promise<PI.PageModel> {
		const {
			atPosition,
			autoCompensate,
			idealHeight,
			idealWidth,
			orientation,
		} = options;

		return new Promise((resolve, reject) => {
			const productModel = ProductStateModule.getProduct;
			let serialnumber = ProductStateModule.getPages.length;
			const offeringModel = ProductStateModule.getOffering;
			let childOfferingModel: DB.OfferingModel | undefined;

			if (!productModel) {
				throw new Error('Could not find required product model');
			}
			if (!offeringModel) {
				throw new Error('Could not find required offering model');
			}

			let offeringId: DB.OfferingModel['id'] | undefined;

			if (
				offeringModel.virtual
				&& ProductStateModule.productSettings.childOfferingTypeId === 'bestFit'
			) {
				if (!idealHeight) {
					throw new Error('Missing required idealHeight');
				}
				if (!idealWidth) {
					throw new Error('Missing required idealWidth');
				}

				// Find the best fitting child offering model
				childOfferingModel = this.findBestVirtualOfferingChild(
					idealWidth,
					idealHeight,
				);

				if (childOfferingModel) {
					offeringId = childOfferingModel.id;
				}
			}

			if (typeof atPosition !== 'undefined') {
				serialnumber = atPosition;
			}

			// create extra pages
			const themePageStateData = ThemeStateModule.getPageStateData(
				serialnumber,
				serialnumber,
				{
					layoutId: productModel.layoutid || undefined,
					offeringId,
					photoOrientation: orientation,
				},
			);

			if (!themePageStateData) {
				throw new Error('Could not setup required theme page state model');
			}

			const pageData: Partial<PI.PageModel> = themePageStateData.pageModel;
			delete pageData.id;
			delete pageData.name;
			delete pageData.productid;
			delete pageData.serialnumber;

			if (ProductStateModule.productId) {
				pageData.productid = ProductStateModule.productId;
			}
			if (offeringId) {
				pageData.offeringId = offeringId;
			}

			const productSettings = ProductStateModule.getProductSettings;

			if (
				typeof productSettings.templateMarginAroundEdge !== 'undefined'
				&& ThemeStateModule.themeHasDynamicLayout
			) {
				pageData.templateMarginAroundEdge = productSettings.templateMarginAroundEdge;
			}
			if (
				typeof productSettings.templateMarginBetweenPositions !== 'undefined'
				&& ThemeStateModule.themeHasDynamicLayout
			) {
				pageData.templateMarginBetweenPositions = productSettings.templateMarginBetweenPositions;
			}

			if (
				pageData.bgcolor != 'transparent'
				&& (
					productSettings.bgcolor
					|| productSettings.bgpattern
				)
			) {
				pageData.bgpattern = productSettings.bgpattern;
				pageData.bgcolor = productSettings.bgcolor;
			}

			// Replace smartTags
			Theme.replaceTags(pageData);

			ProductStateModule
				.addPage({
					data: pageData,
					at: serialnumber,
				})
				.then(
					(newPageModel) => {
						// Copy all the objects on the page
						Page.copyObjects(
							themePageStateData,
							newPageModel,
						);

						// Add template to pagestate
						if (
							newPageModel.template
							&& newPageModel.templateSetId
						) {
							const templateSet = ThemeStateModule.getTemplateSetById(
								newPageModel.template,
								newPageModel.templateSetId,
								{
									marginAroundEdge: newPageModel.templateMarginAroundEdge,
									marginBetweenPositions: newPageModel.templateMarginBetweenPositions,
								},
							);

							if (templateSet) {
								const templatepositions = ProductStateModule
									.getPageTemplatePositionsAvailable({
										pageModel: newPageModel,
										dpi: (
											childOfferingModel?.configdpi
											?? offeringModel.configdpi
										),
									})
									.filter((positionModel) => positionModel.type === 'text') as TemplateTextPosition[];

								templatepositions.forEach((positionModel) => {
									if (positionModel.productattribute) {
										const productAttributeModel = ProductStateModule.getAttributes.find((attributeModel) => (
											attributeModel.property === positionModel.productattribute
										));

										if (productAttributeModel?.value) {
											TemplatePosition
												.fillTextPosition(
													newPageModel,
													positionModel,
													productAttributeModel.value,
												)
												.catch(() => {
													// Swallow error: no action required
												});
										}
									}
								});
							}
						}

						if (!productModel) {
							reject(new Error('Could not find required product model'));
						} else if (!offeringModel) {
							reject(new Error('Could not find required offering model'));
						} else if (
							autoCompensate
							&& (
								(
									// For double sided prints and the memory game, add the backpage as well
									(
										OfferingGroups(
											productModel.group,
											'DoubleSidePrints',
										)
										|| offeringModel.hasback
									)
									&& serialnumber % 2 === 0
								)
								|| (
									// For books always create an even number of pages
									offeringModel.spread
									&& serialnumber > 1
									&& serialnumber % 2 === 0
								)
							)
						) {
							ProductState
								.addPage({
									autoCompensate: true,
									atPosition: serialnumber + 1,
									orientation,
								})
								.then(
									() => {
										resolve(newPageModel);
									},
									reject,
								);
						} else {
							resolve(newPageModel);
						}
					},
					reject,
				);
		});
	}

	static showLayoutSwitch(
		offeringModel: DB.OfferingModel,
	) {
		const countryModel = UserModule.countryid
			? AppDataModule.getCountry(UserModule.countryid)
			: undefined;

		if (ThemeStateModule.themeHasDynamicLayout) {
			const pageOptions = ProductState.getDynamicFillPageOptions(
				ProductStateModule.getPhotosSelected.length,
				offeringModel,
			);
			const marginOptions = ProductState.getDynamicFillMarginOptions(
				offeringModel,
			);
			const orientationOption = ProductState.getDynamicFillOrientation(
				offeringModel,
			);

			UIModule.openDialog({
				body: dialogSlot({
					tag: EditorBookFillView,
					props: {
						currencyModel: (
							UserModule.currency
								? AppDataModule.getCurrency(UserModule.currency)
								: AppDataModule.defaultCurrency
						)!,
						margins: marginOptions,
						orientation: orientationOption,
						pages: pageOptions,
						productLabel: AppDataModule.productGroupName(offeringModel.groupid),
						value: {
							margins: (
								ProductStateModule.getProductSettings.autoFillMarginId
								?? marginOptions[1].id
							),
							pages: (
								ProductStateModule.getProductSettings.autoFillPageDensityId
								?? pageOptions[3].id
							),
						},
						onClose: UIModule.closeDialog,
						'onUpdate:value': (data) => {
							const selectedMarginOption = marginOptions.find(
								(option) => option.id == data.margins,
							);
							const selectedPageOption = pageOptions.find(
								(option) => option.id == data.pages,
							);

							ProductStateModule.changeProductSettings({
								autoFillPageDensityId: selectedPageOption?.id,
								autoFillMarginId: selectedMarginOption?.id,
								templateMarginAroundEdge: (
									selectedMarginOption?.value
									?? 0
								),
								templateMarginBetweenPositions: Math.max(
									1,
									(selectedMarginOption?.value ?? 0) / 2,
								),
							});

							/**
							 * Close the dialog with the margin and pages selection
							 */
							UIModule.closeDialog();

							// Show a loader dialog
							const closeLoader = DialogService.openLoaderDialog();

							// Set heavy load state so parts of the UI are temporarily disabled
							AppStateModule.setHeavyLoad();

							// Debounce to prevent a screen freeze due to an occupied processor
							_.debounce(
								() => {
									ProductState.changeProjectPages(
										true,
										selectedPageOption?.autoFillPageCount,
										true,
									)
										.finally(() => {
											AppStateModule.unsetHeavyLoad();
											ProductStateModule.pushHistory();
											closeLoader();
										});
								},
								100,
							)();
						},
					},
				}),
				props: {
					class: 'my-0 w-275',
					hasCloseButton: false,
					'onClick:outside': UIModule.closeDialog,
				},
			});
		} else if (
			OfferingGroups(
				offeringModel.groupid,
				['PhotoPrints'],
			)
		) {
			const { close: closeDialog } = DialogService.openDialog({
				body: {
					component: VirtualOfferingSelectView,
					props: {
						count: ProductStateModule.getPages.length,
						modus: 'change',
						offeringModel,
						regionId: countryModel?.regionid || 0,
						selectSize: false,
						selectFinish: false,
						selectLayout: true,
					},
					listeners: {
						closeDialog: (event: ServiceEvent<DialogClosePayloadVirtualOfferingSelect>) => {
							if (event.payload?.layoutid) {
								analytics.trackEvent(
									'Change Layout',
									{
										category: 'Button',
									},
								);

								const closeLoader = DialogService.openLoaderDialog();

								ProductState
									.changeLayout(
										event.payload.layoutid,
										true,
									)
									.then(() => (
										ProductStateModule.pushHistory()
									))
									.finally(closeLoader);
							}

							closeDialog();
						},
					},
					styles: {
						padding: '0',
					},
				},
			});
		} else {
			const { close: closeDialog } = DialogService.openDialog({
				header: {
					classes: 'picker',
					title: window.App.router.$t('pickerLayout'),
				},
				body: {
					component: LayoutPickerView,
					listeners: {
						closeDialog: () => {
							closeDialog();
						},
					},
				},
				width: 500,
			});
		}
	}
}
